// src/get-conflicting-output-paths.ts
var getConflictingOutputPaths = (paths) => {
  const files = new Map;
  for (const path of paths) {
    const key = path.normalize("NFD").toLowerCase().toUpperCase().normalize("NFC");
    const existing = files.get(key);
    if (existing !== undefined) {
      return [existing, path];
    }
    files.set(key, path);
  }
  for (const [key, path] of files) {
    let slash = key.indexOf("/");
    while (slash !== -1) {
      const parentFile = files.get(key.slice(0, slash));
      if (parentFile !== undefined) {
        return [parentFile, path];
      }
      slash = key.indexOf("/", slash + 1);
    }
  }
  return null;
};
// src/validate-studio-artifact-filename.ts
var windowsDeviceName = /^(?:con|prn|aux|nul|clock\$|conin\$|conout\$|com[0-9¹²³]|lpt[0-9¹²³]) *(?:\.|$)/i;
var windowsShortName = /^[^.]{1,6}~\d+(?:\.[^.]{1,3})?$/;
var getPortableDownloadFilename = (filename) => {
  const name = filename.replace(/[\p{Cc}\p{Cs}<>:"\\/|?*]/gu, "_").replace(/^ +|[. ]+$/g, "");
  if (!name || windowsDeviceName.test(name)) {
    return "_" + name;
  }
  return windowsShortName.test(name) ? name.replace("~", "_") : name;
};
var validateStudioArtifactFilename = (filename) => {
  for (const segment of filename.split("/")) {
    if (getPortableDownloadFilename(segment) !== segment) {
      throw new Error(`Invalid render artifact filename: ${filename}`);
    }
  }
};
// src/api-requests.ts
class StudioOperation {
  mutatesSource;
  constructor({ mutatesSource }) {
    this.mutatesSource = mutatesSource;
  }
}
var studioOperations = {
  "/api/shared-memory-capture-support": new StudioOperation({ mutatesSource: false }),
  "/api/invalidate-bundle": new StudioOperation({ mutatesSource: true }),
  "/api/composition-component-info": new StudioOperation({ mutatesSource: false }),
  "/api/cancel": new StudioOperation({ mutatesSource: false }),
  "/api/render": new StudioOperation({
    mutatesSource: false
  }),
  "/api/unsubscribe-from-file-existence": new StudioOperation({ mutatesSource: false }),
  "/api/subscribe-to-file-existence": new StudioOperation({ mutatesSource: false }),
  "/api/remove-render": new StudioOperation({
    mutatesSource: false
  }),
  "/api/open-in-editor": new StudioOperation({ mutatesSource: false }),
  "/api/open-in-coding-agent": new StudioOperation({ mutatesSource: false }),
  "/api/app-info": new StudioOperation({
    mutatesSource: false
  }),
  "/api/find-in-file": new StudioOperation({ mutatesSource: false }),
  "/api/open-in-file-explorer": new StudioOperation({ mutatesSource: false }),
  "/api/open-in-terminal": new StudioOperation({ mutatesSource: false }),
  "/api/open-in-git-client": new StudioOperation({ mutatesSource: false }),
  "/api/register-client-render": new StudioOperation({ mutatesSource: false }),
  "/api/unregister-client-render": new StudioOperation({
    mutatesSource: false
  }),
  "/api/update-default-props": new StudioOperation({ mutatesSource: true }),
  "/api/apply-visual-control-change": new StudioOperation({ mutatesSource: true }),
  "/api/subscribe-to-default-props": new StudioOperation({ mutatesSource: false }),
  "/api/unsubscribe-from-default-props": new StudioOperation({ mutatesSource: false }),
  "/api/subscribe-to-sequence-props": new StudioOperation({ mutatesSource: false }),
  "/api/unsubscribe-from-sequence-props": new StudioOperation({ mutatesSource: false }),
  "/api/save-sequence-props": new StudioOperation({ mutatesSource: true }),
  "/api/save-effect-props": new StudioOperation({ mutatesSource: true }),
  "/api/save-multiple-effect-props": new StudioOperation({ mutatesSource: true }),
  "/api/add-effect": new StudioOperation({
    mutatesSource: true
  }),
  "/api/reorder-effect": new StudioOperation({ mutatesSource: true }),
  "/api/duplicate-effect": new StudioOperation({ mutatesSource: true }),
  "/api/reorder-sequence": new StudioOperation({ mutatesSource: true }),
  "/api/delete-keyframes": new StudioOperation({ mutatesSource: true }),
  "/api/move-keyframes": new StudioOperation({ mutatesSource: true }),
  "/api/add-sequence-keyframe": new StudioOperation({ mutatesSource: true }),
  "/api/add-effect-keyframe": new StudioOperation({ mutatesSource: true }),
  "/api/add-keyframes": new StudioOperation({ mutatesSource: true }),
  "/api/update-sequence-keyframe-settings": new StudioOperation({ mutatesSource: true }),
  "/api/update-effect-keyframe-settings": new StudioOperation({ mutatesSource: true }),
  "/api/batch-update-keyframe-settings": new StudioOperation({ mutatesSource: true }),
  "/api/delete-effect": new StudioOperation({ mutatesSource: true }),
  "/api/paste-effects": new StudioOperation({ mutatesSource: true }),
  "/api/delete-nodes": new StudioOperation({ mutatesSource: true }),
  "/api/duplicate-nodes": new StudioOperation({ mutatesSource: true }),
  "/api/precompose-jsx-nodes": new StudioOperation({ mutatesSource: true }),
  "/api/unwrap-node": new StudioOperation({ mutatesSource: true }),
  "/api/wrap-node": new StudioOperation({
    mutatesSource: true
  }),
  "/api/split-sequences": new StudioOperation({ mutatesSource: true }),
  "/api/split-video-from-audio": new StudioOperation({ mutatesSource: true }),
  "/api/insert-basic-captions": new StudioOperation({ mutatesSource: true }),
  "/api/replace-video-source": new StudioOperation({ mutatesSource: true }),
  "/api/prerendered-scene-info": new StudioOperation({ mutatesSource: false }),
  "/api/restore-prerendered-scene": new StudioOperation({ mutatesSource: true }),
  "/api/insert-composition-element": new StudioOperation({ mutatesSource: true }),
  "/api/convert-figma-clipboard-to-svg": new StudioOperation({ mutatesSource: false }),
  "/api/insert-element": new StudioOperation({ mutatesSource: true }),
  "/api/prepare-element-install": new StudioOperation({ mutatesSource: false }),
  "/api/update-element-install-target": new StudioOperation({ mutatesSource: false }),
  "/api/download-remote-asset": new StudioOperation({ mutatesSource: false }),
  "/api/update-available": new StudioOperation({ mutatesSource: false }),
  "/api/release-notes": new StudioOperation({ mutatesSource: false }),
  "/api/remotion-skills-info": new StudioOperation({ mutatesSource: false }),
  "/api/install-remotion-skill": new StudioOperation({ mutatesSource: false }),
  "/api/remove-remotion-skill": new StudioOperation({ mutatesSource: false }),
  "/api/upgrade-remotion-skill": new StudioOperation({ mutatesSource: false }),
  "/api/open-remotion-skill": new StudioOperation({ mutatesSource: false }),
  "/api/add-composition": new StudioOperation({ mutatesSource: true }),
  "/api/duplicate-composition": new StudioOperation({ mutatesSource: true }),
  "/api/rename-composition": new StudioOperation({ mutatesSource: true }),
  "/api/update-composition-metadata": new StudioOperation({ mutatesSource: true }),
  "/api/delete-composition": new StudioOperation({ mutatesSource: true }),
  "/api/move-composition": new StudioOperation({ mutatesSource: true }),
  "/api/add-folder": new StudioOperation({ mutatesSource: true }),
  "/api/rename-folder": new StudioOperation({ mutatesSource: true }),
  "/api/unwrap-folder": new StudioOperation({ mutatesSource: true }),
  "/api/move-folder": new StudioOperation({ mutatesSource: true }),
  "/api/project-info": new StudioOperation({ mutatesSource: false }),
  "/api/delete-static-file": new StudioOperation({ mutatesSource: false }),
  "/api/rename-static-file": new StudioOperation({ mutatesSource: false }),
  "/api/copy-render-output-to-asset": new StudioOperation({ mutatesSource: false }),
  "/api/upgrade-remotion": new StudioOperation({
    mutatesSource: false
  }),
  "/api/shutdown-studio": new StudioOperation({
    mutatesSource: false
  }),
  "/api/restart-studio": new StudioOperation({ mutatesSource: false }),
  "/api/update-config": new StudioOperation({ mutatesSource: false }),
  "/api/install-package": new StudioOperation({ mutatesSource: false }),
  "/api/undo": new StudioOperation({
    mutatesSource: true
  }),
  "/api/redo": new StudioOperation({
    mutatesSource: true
  }),
  "/api/log-studio-error": new StudioOperation({ mutatesSource: false })
};
// src/ansi.ts
var ansiRegex = () => {
  const pattern = [
    "[\\u001B\\u009B][[\\]()#;?]*(?:(?:(?:(?:;[-a-zA-Z\\d\\/#&.:=?%@~_]+)*|[a-zA-Z\\d]+(?:;[-a-zA-Z\\d\\/#&.:=?%@~_]*)*)?\\u0007)",
    "(?:(?:\\d{1,4}(?:;\\d{0,4})*)?[\\dA-PR-TZcf-nq-uy=><~]))"
  ].join("|");
  return new RegExp(pattern, "g");
};
function splitAnsi(str) {
  const parts = str.match(ansiRegex());
  if (!parts)
    return [str];
  const result = [];
  let offset = 0;
  let ptr = 0;
  for (let i = 0;i < parts.length; i++) {
    offset = str.indexOf(parts[i], offset);
    if (offset === -1)
      throw new Error("Could not split string");
    if (ptr !== offset)
      result.push(str.slice(ptr, offset));
    if (ptr === offset && result.length) {
      result[result.length - 1] += parts[i];
    } else {
      if (offset === 0)
        result.push("");
      result.push(parts[i]);
    }
    ptr = offset + parts[i].length;
  }
  result.push(str.slice(ptr));
  return result;
}
var stripAnsi = (str) => {
  if (typeof str !== "string") {
    throw new TypeError(`Expected a \`string\`, got \`${typeof str}\``);
  }
  return str.replace(ansiRegex(), "");
};
// src/canvas-capture.ts
var CANVAS_CAPTURE_METADATA_TAG = "REMOTION_CAPTURE_DATA";
var isRecord = (value) => {
  return typeof value === "object" && value !== null;
};
var isFiniteNumber = (value) => {
  return typeof value === "number" && Number.isFinite(value);
};
var parseCanvasCaptureData = (metadata) => {
  if (!isRecord(metadata) || !isRecord(metadata.raw)) {
    return null;
  }
  const rawCaptureData = metadata.raw[CANVAS_CAPTURE_METADATA_TAG];
  if (typeof rawCaptureData !== "string") {
    return null;
  }
  let parsed;
  try {
    parsed = JSON.parse(rawCaptureData);
  } catch {
    return null;
  }
  if (!isRecord(parsed) || !isRecord(parsed.captureMetadata) || !isFiniteNumber(parsed.captureMetadata.density) || parsed.captureMetadata.density <= 0 || !Array.isArray(parsed.mouseMovements) || !Array.isArray(parsed.pointerClicks)) {
    return null;
  }
  const mouseMovements = [];
  for (const movement of parsed.mouseMovements) {
    if (!isRecord(movement) || !isFiniteNumber(movement.timeInSeconds) || movement.timeInSeconds < 0 || movement.canvasX !== null && !isFiniteNumber(movement.canvasX) || movement.canvasY !== null && !isFiniteNumber(movement.canvasY) || typeof movement.cursor !== "string") {
      return null;
    }
    mouseMovements.push({
      timeInSeconds: movement.timeInSeconds,
      canvasX: movement.canvasX,
      canvasY: movement.canvasY,
      cursor: movement.cursor
    });
  }
  const pointerClicks = [];
  for (const click of parsed.pointerClicks) {
    if (!isRecord(click) || !isFiniteNumber(click.timeInSeconds) || click.timeInSeconds < 0 || click.type !== "pointer-down" && click.type !== "pointer-up") {
      return null;
    }
    pointerClicks.push({
      timeInSeconds: click.timeInSeconds,
      type: click.type
    });
  }
  return {
    captureMetadata: { density: parsed.captureMetadata.density },
    mouseMovements: mouseMovements.sort((a, b) => a.timeInSeconds - b.timeInSeconds),
    pointerClicks: pointerClicks.sort((a, b) => a.timeInSeconds - b.timeInSeconds)
  };
};
// src/react-refresh-event.ts
import { Internals } from "remotion";
var {
  REACT_REFRESH_STARTED_EVENT,
  REACT_REFRESH_FINISHED_EVENT
} = Internals;
// src/has-sequence-timing-traits.ts
var sequenceTimingTraits = ["from", "durationInFrames", "trimBefore"];
var hasSequenceTimingTraits = (sequenceKeys) => {
  return sequenceTimingTraits.every((trait) => sequenceKeys.includes(trait));
};
// src/config-file-change.ts
var getConfigFileChangeMessage = (changeType) => {
  if (changeType === "restart") {
    return "Restart Studio to apply config file changes";
  }
  if (changeType === "reload") {
    return "Reload page to apply config file changes";
  }
  return "Config file changed";
};
// src/config-method-lifecycles.ts
var configMethodLifecycles = {
  addElementLibrary: "runtime",
  overrideBundlerConfig: "restart",
  overrideDuration: "runtime",
  overrideFfmpegCommand: "runtime",
  overrideFps: "runtime",
  overrideHeight: "runtime",
  overrideRspackConfig: "restart",
  overrideWebpackConfig: "restart",
  overrideWidth: "runtime",
  setAllowHtmlInCanvasEnabled: "runtime",
  setAskAIEnabled: "runtime",
  setAudioBitrate: "runtime",
  setAudioCodec: "runtime",
  setAudioLatencyHint: "reload",
  setBeepOnFinish: "runtime",
  setBenchmarkConcurrencies: "runtime",
  setBenchmarkRuns: "runtime",
  setBinariesDirectory: "restart",
  setBrowserExecutable: "runtime",
  setBufferStateDelayInMilliseconds: "runtime",
  setBundleOutDir: "runtime",
  setCachingEnabled: "runtime",
  setCanvasTabsEnabled: "runtime",
  setChromeMode: "runtime",
  setChromiumDarkMode: "runtime",
  setChromiumDisableWebSecurity: "runtime",
  setChromiumHeadlessMode: "runtime",
  setChromiumIgnoreCertificateErrors: "runtime",
  setChromiumMultiProcessOnLinux: "runtime",
  setChromiumOpenGlRenderer: "runtime",
  setChromiumUserAgent: "runtime",
  setCodec: "runtime",
  setColorSpace: "runtime",
  setConcurrency: "runtime",
  setCrf: "runtime",
  setDefaultCodingAgent: "runtime",
  setDefaultEditor: "runtime",
  setDefaultPremountInSeconds: "runtime",
  setDelayRenderTimeoutInMilliseconds: "runtime",
  setDeleteAfter: "runtime",
  setDisallowParallelEncoding: "runtime",
  setDisableSharedMemoryCapture: "runtime",
  setDotEnvLocation: "restart",
  setEnableCancellation: "runtime",
  setEnableCrossSiteIsolation: "restart",
  setEnableFolderExpiry: "runtime",
  setEncodingBufferSize: "runtime",
  setEncodingMaxRate: "runtime",
  setEnforceAudioTrack: "runtime",
  setEntryPoint: "restart",
  setEveryNthFrame: "runtime",
  setExperimentalKeepAudioContextAlive: "reload",
  setExperimentalRspackEnabled: "restart",
  setSequenceActivityLimit: "runtime",
  setForSeamlessAacConcatenation: "runtime",
  setForceNewStudioEnabled: "restart",
  setFrameRange: "runtime",
  setGopSize: "runtime",
  setHardwareAcceleration: "runtime",
  setIPv4: "restart",
  setImageFormat: "runtime",
  setImageSequence: "runtime",
  setImageSequencePattern: "runtime",
  setInteractivityEnabled: "runtime",
  setJpegQuality: "runtime",
  setKeyboardShortcutsEnabled: "runtime",
  setKeyboardShortcuts: "runtime",
  setLambdaInsights: "runtime",
  setLevel: "restart",
  setLogLevel: "restart",
  setMaxTimelineTracks: "runtime",
  setMetadata: "runtime",
  setMuted: "runtime",
  setNumberOfGifLoops: "runtime",
  setNumberOfSharedAudioTags: "reload",
  setOffthreadVideoCacheSizeInBytes: "runtime",
  setOutputLocation: "runtime",
  setOverwriteOutput: "runtime",
  setPixelFormat: "runtime",
  setPort: "restart",
  setPreferLosslessAudio: "runtime",
  setPreservePitchOnPlayback: "runtime",
  setPreviewSampleRate: "reload",
  setPreferredEffectBackend: "reload",
  setProResProfile: "runtime",
  setPublicDir: "restart",
  setPublicLicenseKey: "runtime",
  setPublicPath: "runtime",
  setQuality: "runtime",
  setRendererPort: "restart",
  setRepro: "runtime",
  setRspack: "restart",
  setSampleRate: "runtime",
  setScale: "runtime",
  setShouldOpenBrowser: "restart",
  setShowLayerFrameNumbers: "runtime",
  setShowPremounting: "runtime",
  setStillImageFormat: "runtime",
  setStudioPort: "restart",
  setTimeoutInMilliseconds: "runtime",
  setVideoBitrate: "runtime",
  setVideoImageFormat: "runtime",
  setWebpackPollingInMilliseconds: "restart",
  setX264Preset: "runtime"
};
// src/keyboard-shortcuts.ts
var studioKeyboardShortcutActions = [
  "playPause",
  "toggleMute",
  "toggleLoop",
  "jumpToBeginning",
  "jumpToEnd",
  "reversePlayback",
  "pausePlayback",
  "playForward",
  "decreasePlaybackRate",
  "increasePlaybackRate",
  "goToFrame",
  "pauseAndReturnToPlaybackStart",
  "toggleLeftSidebar",
  "toggleRightSidebar",
  "toggleBothSidebars",
  "enterFullscreen",
  "toggleSnapping",
  "toggleOutlines",
  "toggleRulersAndGuides",
  "togglePixelGrid",
  "previousComposition",
  "nextComposition",
  "newComposition",
  "showKeyboardShortcuts",
  "quickSwitcher",
  "render",
  "toggleCheckerboard",
  "setInPoint",
  "setOutPoint",
  "clearInOutPoints",
  "zoomIn",
  "zoomOut",
  "resetZoom",
  "undo",
  "redo",
  "selectAllSequenceRows",
  "selectTranslateProp",
  "selectOpacityProp",
  "selectRotateProp",
  "selectScaleProp",
  "selectVolumeProp",
  "duplicateSequences",
  "splitSequences",
  "copyEffectsAndValues",
  "cutEffects",
  "deleteSelection",
  "askAI",
  "pickColor"
];
var actionSet = new Set(studioKeyboardShortcutActions);
var validateShortcut = ({
  action,
  shortcut
}) => {
  if (typeof shortcut !== "object" || shortcut === null || Array.isArray(shortcut)) {
    return `The keyboard shortcut for ${JSON.stringify(action)} must be an object.`;
  }
  const value = shortcut;
  const allowedProperties = new Set([
    "key",
    "commandOrControl",
    "shift",
    "alt"
  ]);
  for (const property of Object.keys(value)) {
    if (!allowedProperties.has(property)) {
      return `Unknown keyboard shortcut property ${JSON.stringify(property)} for ${JSON.stringify(action)}.`;
    }
  }
  if (typeof value.key !== "string" || value.key.length === 0) {
    return `The keyboard shortcut for ${JSON.stringify(action)} must have a non-empty "key".`;
  }
  for (const modifier of ["commandOrControl", "shift", "alt"]) {
    if (value[modifier] !== undefined && typeof value[modifier] !== "boolean") {
      return `The "${modifier}" property for ${JSON.stringify(action)} must be a boolean.`;
    }
  }
  return null;
};
var validateStudioKeyboardShortcuts = (value) => {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return "Config.setKeyboardShortcuts() expects an object.";
  }
  for (const [action, shortcutOrShortcuts] of Object.entries(value)) {
    if (!actionSet.has(action)) {
      return `Unknown Studio keyboard shortcut action: ${JSON.stringify(action)}.`;
    }
    if (shortcutOrShortcuts === null) {
      continue;
    }
    const shortcuts = Array.isArray(shortcutOrShortcuts) ? shortcutOrShortcuts : [shortcutOrShortcuts];
    if (shortcuts.length === 0) {
      return `The keyboard shortcut list for ${JSON.stringify(action)} must not be empty. Use null to disable it.`;
    }
    for (const shortcut of shortcuts) {
      const error = validateShortcut({ action, shortcut });
      if (error !== null) {
        return error;
      }
    }
  }
  return null;
};
// src/default-buffer-state-delay-in-milliseconds.ts
var DEFAULT_BUFFER_STATE_DELAY_IN_MILLISECONDS = 300;
// src/detect-file-type.ts
var webmPattern = new Uint8Array([26, 69, 223, 163]);
var matchesPattern = (pattern) => {
  return (data) => {
    return pattern.every((value, index) => data[index] === value);
  };
};
var isRiffAvi = (data) => {
  const riffPattern = new Uint8Array([82, 73, 70, 70]);
  if (!matchesPattern(riffPattern)(data.subarray(0, 4))) {
    return false;
  }
  const fileType = data.subarray(8, 12);
  const aviPattern = new Uint8Array([65, 86, 73, 32]);
  return matchesPattern(aviPattern)(fileType);
};
var isRiffWave = (data) => {
  const riffPattern = new Uint8Array([82, 73, 70, 70]);
  if (!matchesPattern(riffPattern)(data.subarray(0, 4))) {
    return false;
  }
  const fileType = data.subarray(8, 12);
  const wavePattern = new Uint8Array([87, 65, 86, 69]);
  return matchesPattern(wavePattern)(fileType);
};
var isWebm = (data) => {
  return matchesPattern(webmPattern)(data.subarray(0, 4));
};
var isIsoBaseMedia = (data) => {
  const isoBaseMediaMp4Pattern = new TextEncoder().encode("ftyp");
  return matchesPattern(isoBaseMediaMp4Pattern)(data.subarray(4, 8));
};
var isTransportStream = (data) => {
  return data[0] === 71 && data[188] === 71;
};
var isMp3 = (data) => {
  const mpegPattern = new Uint8Array([255, 243]);
  const mpegPattern2 = new Uint8Array([255, 251]);
  const id3v4Pattern = new Uint8Array([73, 68, 51, 4]);
  const id3v3Pattern = new Uint8Array([73, 68, 51, 3]);
  const id3v2Pattern = new Uint8Array([73, 68, 51, 2]);
  const subarray = data.subarray(0, 4);
  return matchesPattern(mpegPattern)(subarray) || matchesPattern(mpegPattern2)(subarray) || matchesPattern(id3v4Pattern)(subarray) || matchesPattern(id3v3Pattern)(subarray) || matchesPattern(id3v2Pattern)(subarray);
};
var isAac = (data) => {
  const aacPattern = new Uint8Array([255, 241]);
  return matchesPattern(aacPattern)(data.subarray(0, 2));
};
var isFlac = (data) => {
  const flacPattern = new Uint8Array([102, 76, 97, 67]);
  return matchesPattern(flacPattern)(data.subarray(0, 4));
};
var isM3u = (data) => {
  return new TextDecoder("utf-8").decode(data.slice(0, 7)) === "#EXTM3U";
};
var pngSignature = [137, 80, 78, 71, 13, 10, 26, 10];
var acTLChunkType = new Uint8Array([97, 99, 84, 76]);
var idatChunkType = new Uint8Array([73, 68, 65, 84]);
var iendChunkType = new Uint8Array([73, 69, 78, 68]);
var getPngDimensions = (pngData) => {
  if (pngData.length < 24) {
    return null;
  }
  const view = new DataView(pngData.buffer, pngData.byteOffset);
  for (let i = 0;i < 8; i++) {
    if (pngData[i] !== pngSignature[i]) {
      return null;
    }
  }
  return {
    width: view.getUint32(16, false),
    height: view.getUint32(20, false)
  };
};
var hasApngAnimationControlChunk = (pngData) => {
  if (pngData.length < 16) {
    return false;
  }
  const view = new DataView(pngData.buffer, pngData.byteOffset, pngData.byteLength);
  let offset = 8;
  while (offset + 8 <= pngData.length) {
    const chunkLength = view.getUint32(offset, false);
    const chunkType = pngData.subarray(offset + 4, offset + 8);
    if (matchesPattern(acTLChunkType)(chunkType)) {
      return true;
    }
    if (matchesPattern(idatChunkType)(chunkType) || matchesPattern(iendChunkType)(chunkType)) {
      return false;
    }
    const nextOffset = offset + 12 + chunkLength;
    if (nextOffset <= offset) {
      return false;
    }
    offset = nextOffset;
  }
  return false;
};
var isPng = (data) => {
  const pngPattern = new Uint8Array([137, 80, 78, 71]);
  if (matchesPattern(pngPattern)(data.subarray(0, 4))) {
    const png = getPngDimensions(data);
    return {
      dimensions: png,
      type: hasApngAnimationControlChunk(data) ? "apng" : "png"
    };
  }
  return null;
};
var getJpegDimensions = (data) => {
  let offset = 0;
  const readUint16BE = (o) => {
    return data[o] << 8 | data[o + 1];
  };
  if (data.length < 4 || readUint16BE(offset) !== 65496) {
    return null;
  }
  offset += 2;
  while (offset + 3 < data.length) {
    if (data[offset] === 255) {
      const marker = data[offset + 1];
      if (marker === 192 || marker === 194) {
        if (offset + 8 >= data.length) {
          return null;
        }
        const height = readUint16BE(offset + 5);
        const width = readUint16BE(offset + 7);
        return { width, height };
      }
      const length = readUint16BE(offset + 2);
      if (length <= 0) {
        return null;
      }
      offset += length + 2;
    } else {
      offset++;
    }
  }
  return null;
};
var isJpeg = (data) => {
  const jpegPattern = new Uint8Array([255, 216]);
  const jpeg = matchesPattern(jpegPattern)(data.subarray(0, 2));
  if (!jpeg) {
    return null;
  }
  const dim = getJpegDimensions(data);
  return { dimensions: dim, type: "jpeg" };
};
var getJxlDimensions = (data) => {
  let codestream = data;
  if (data[0] !== 255 || data[1] !== 10) {
    const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    const header = new Uint8Array(16);
    let headerLength = 0;
    let offset = 12;
    let partialIndex = 0;
    while (offset + 8 <= data.length) {
      let boxSize = view.getUint32(offset, false);
      const boxType = view.getUint32(offset + 4, false);
      let boxHeaderSize = 8;
      if (boxSize === 1) {
        if (offset + 16 > data.length) {
          return null;
        }
        boxSize = view.getUint32(offset + 8, false) * 2 ** 32 + view.getUint32(offset + 12, false);
        boxHeaderSize = 16;
      } else if (boxSize === 0) {
        boxSize = data.length - offset;
      }
      if (!Number.isSafeInteger(boxSize) || boxSize < boxHeaderSize || boxSize > data.length - offset) {
        return null;
      }
      const end = offset + boxSize;
      if (boxType === 1786276963) {
        if (partialIndex !== 0) {
          return null;
        }
        codestream = data.subarray(offset + boxHeaderSize, end);
        break;
      }
      if (boxType === 1786276976) {
        if (boxSize < boxHeaderSize + 4) {
          return null;
        }
        const index = view.getUint32(offset + boxHeaderSize, false);
        if ((index & 2147483647) !== partialIndex) {
          return null;
        }
        partialIndex++;
        const bytes = data.subarray(offset + boxHeaderSize + 4, Math.min(end, offset + boxHeaderSize + 4 + 16 - headerLength));
        header.set(bytes, headerLength);
        headerLength += bytes.length;
        codestream = header.subarray(0, headerLength);
        if (headerLength === header.length || (index & 2147483648) !== 0) {
          break;
        }
      }
      offset = end;
    }
  }
  if (codestream[0] !== 255 || codestream[1] !== 10) {
    return null;
  }
  let bitOffset = 16;
  let truncated = false;
  const readBits = (count) => {
    if (bitOffset + count > codestream.length * 8) {
      truncated = true;
      return 0;
    }
    let value = 0;
    for (let bit = 0;bit < count; bit++) {
      value += (codestream[bitOffset >> 3] >> (bitOffset & 7) & 1) * 2 ** bit;
      bitOffset++;
    }
    return value;
  };
  const small = readBits(1) === 1;
  const height = small ? (readBits(5) + 1) * 8 : readBits([9, 13, 18, 30][readBits(2)]) + 1;
  const ratio = readBits(3);
  const width = ratio === 0 ? small ? (readBits(5) + 1) * 8 : readBits([9, 13, 18, 30][readBits(2)]) + 1 : Math.floor(height * [0, 1, 12, 4, 3, 16, 5, 2][ratio] / [1, 1, 10, 3, 2, 9, 4, 1][ratio]);
  const orientation = readBits(1) === 0 && readBits(1) === 1 ? readBits(3) + 1 : 1;
  if (truncated) {
    return null;
  }
  return orientation > 4 ? { width: height, height: width } : { width, height };
};
var isJxl = (data) => {
  const codestreamPattern = new Uint8Array([255, 10]);
  const containerPattern = new Uint8Array([
    0,
    0,
    0,
    12,
    74,
    88,
    76,
    32,
    13,
    10,
    135,
    10
  ]);
  if (matchesPattern(codestreamPattern)(data) || matchesPattern(containerPattern)(data)) {
    return { type: "jxl", dimensions: getJxlDimensions(data) };
  }
  return null;
};
var getGifDimensions = (data) => {
  if (data.length < 10) {
    return null;
  }
  const view = new DataView(data.buffer, data.byteOffset);
  const width = view.getUint16(6, true);
  const height = view.getUint16(8, true);
  return { width, height };
};
var isGif = (data) => {
  const gifPattern = new Uint8Array([71, 73, 70, 56]);
  if (matchesPattern(gifPattern)(data.subarray(0, 4))) {
    return { type: "gif", dimensions: getGifDimensions(data) };
  }
  return null;
};
var getWebPDimensions = (bytes) => {
  if (bytes.length < 30) {
    return null;
  }
  if (bytes[0] !== 82 || bytes[1] !== 73 || bytes[2] !== 70 || bytes[3] !== 70 || bytes[8] !== 87 || bytes[9] !== 69 || bytes[10] !== 66 || bytes[11] !== 80) {
    return null;
  }
  if (bytes[12] === 86 && bytes[13] === 80 && bytes[14] === 56) {
    if (bytes[15] === 32) {
      return {
        width: bytes[26] | bytes[27] << 8 & 16383,
        height: bytes[28] | bytes[29] << 8 & 16383
      };
    }
  }
  if (bytes[12] === 86 && bytes[13] === 80 && bytes[14] === 56 && bytes[15] === 76) {
    return {
      width: 1 + (bytes[21] | (bytes[22] & 63) << 8),
      height: 1 + ((bytes[22] & 192) >> 6 | bytes[23] << 2 | (bytes[24] & 15) << 10)
    };
  }
  if (bytes[12] === 86 && bytes[13] === 80 && bytes[14] === 56 && bytes[15] === 88) {
    return {
      width: 1 + (bytes[24] | bytes[25] << 8 | bytes[26] << 16),
      height: 1 + (bytes[27] | bytes[28] << 8 | bytes[29] << 16)
    };
  }
  return null;
};
var isAnimatedWebp = (data) => {
  const animationChunk = new Uint8Array([65, 78, 73, 77]);
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  let offset = 12;
  while (offset + 8 <= data.length) {
    const chunkType = data.subarray(offset, offset + 4);
    if (matchesPattern(animationChunk)(chunkType)) {
      return true;
    }
    const chunkLength = view.getUint32(offset + 4, true);
    const nextOffset = offset + 8 + chunkLength + chunkLength % 2;
    if (nextOffset <= offset) {
      return false;
    }
    offset = nextOffset;
  }
  return false;
};
var isWebp = (data) => {
  const riffPattern = new Uint8Array([82, 73, 70, 70]);
  const webpPattern = new Uint8Array([87, 69, 66, 80]);
  if (matchesPattern(riffPattern)(data.subarray(0, 4)) && matchesPattern(webpPattern)(data.subarray(8, 12))) {
    return {
      type: "webp",
      dimensions: getWebPDimensions(data),
      animated: isAnimatedWebp(data)
    };
  }
  return null;
};
var getBmpDimensions = (bmpData) => {
  if (bmpData.length < 26) {
    return null;
  }
  const view = new DataView(bmpData.buffer, bmpData.byteOffset);
  return {
    width: view.getUint32(18, true),
    height: Math.abs(view.getInt32(22, true))
  };
};
var isBmp = (data) => {
  const bmpPattern = new Uint8Array([66, 77]);
  if (matchesPattern(bmpPattern)(data.subarray(0, 2))) {
    const bmp = getBmpDimensions(data);
    return { dimensions: bmp, type: "bmp" };
  }
  return null;
};
var isPdf = (data) => {
  if (data.length < 4) {
    return null;
  }
  const pdfPattern = new Uint8Array([37, 80, 68, 70]);
  return matchesPattern(pdfPattern)(data.subarray(0, 4)) ? { type: "pdf" } : null;
};
var isImageFileType = (fileType) => {
  return fileType.type === "jpeg" || fileType.type === "jxl" || fileType.type === "webp" || fileType.type === "gif" || fileType.type === "png" || fileType.type === "apng" || fileType.type === "bmp";
};
var detectFileType = (data) => {
  const jxl = isJxl(data);
  if (jxl) {
    return jxl;
  }
  if (isRiffWave(data)) {
    return { type: "wav" };
  }
  if (isRiffAvi(data)) {
    return { type: "riff" };
  }
  if (isAac(data)) {
    return { type: "aac" };
  }
  if (isFlac(data)) {
    return { type: "flac" };
  }
  if (isM3u(data)) {
    return { type: "m3u" };
  }
  const webp = isWebp(data);
  if (webp) {
    return webp;
  }
  if (isWebm(data)) {
    return { type: "webm" };
  }
  if (isIsoBaseMedia(data)) {
    return { type: "iso-base-media" };
  }
  if (isTransportStream(data)) {
    return { type: "transport-stream" };
  }
  if (isMp3(data)) {
    return { type: "mp3" };
  }
  const gif = isGif(data);
  if (gif) {
    return gif;
  }
  const png = isPng(data);
  if (png) {
    return png;
  }
  const pdf = isPdf(data);
  if (pdf) {
    return pdf;
  }
  const bmp = isBmp(data);
  if (bmp) {
    return bmp;
  }
  const jpeg = isJpeg(data);
  if (jpeg) {
    return jpeg;
  }
  return { type: "unknown" };
};
// src/remote-asset.ts
var maxRemoteAssetSize = 50 * 1024 * 1024;
var remoteAssetDownloadTimeout = 15000;
var remoteAssetAcceptHeader = "image/png,image/apng,image/jpeg,image/jxl,image/webp,image/bmp,image/gif";
var extensionsForFileType = {
  png: ["png"],
  apng: ["png", "apng"],
  jpeg: ["jpg", "jpeg"],
  jxl: ["jxl"],
  webp: ["webp"],
  bmp: ["bmp"],
  gif: ["gif"]
};
var getRemoteAssetFilename = ({
  fileType,
  url
}) => {
  const encodedBasename = url.pathname.slice(url.pathname.lastIndexOf("/") + 1);
  let basename;
  try {
    basename = decodeURIComponent(encodedBasename);
  } catch {
    basename = encodedBasename;
  }
  const sanitized = Array.from(basename).map((character) => {
    const charCode = character.charCodeAt(0);
    return charCode <= 31 || '<>:"/\\|?*'.includes(character) ? "-" : character;
  }).join("").trim().replace(/^[. ]+|[. ]+$/g, "");
  const filenameWithoutFallback = sanitized === "" ? "image" : sanitized;
  const extensions = extensionsForFileType[fileType.type];
  const lastDot = filenameWithoutFallback.lastIndexOf(".");
  const extension = lastDot > 0 ? filenameWithoutFallback.slice(lastDot + 1).toLowerCase() : "";
  if (extensions.includes(extension)) {
    return filenameWithoutFallback;
  }
  const withoutExtension = extension ? filenameWithoutFallback.slice(0, -(extension.length + 1)) : filenameWithoutFallback;
  const safeName = withoutExtension === "" ? "image" : withoutExtension;
  return `${safeName}.${extensions[0]}`;
};
var getRemoteAssetElement = ({
  assetPath,
  fileType
}) => ({
  type: "asset",
  assetType: fileType.type === "gif" ? "gif" : fileType.type === "apng" || fileType.type === "webp" && fileType.animated ? "animated-image" : "image",
  src: assetPath,
  srcType: "static",
  dimensions: fileType.dimensions,
  durationInFrames: null,
  position: null
});
// src/easing-clipboard-data.ts
var isRecord2 = (value) => {
  return typeof value === "object" && value !== null && !Array.isArray(value);
};
var isFiniteNumber2 = (value) => {
  return typeof value === "number" && Number.isFinite(value);
};
var isKeyframeEasing = (value) => {
  return isRecord2(value) && (value.type === "linear" || value.type === "step1" || value.type === "bezier" && isFiniteNumber2(value.x1) && isFiniteNumber2(value.y1) && isFiniteNumber2(value.x2) && isFiniteNumber2(value.y2) || value.type === "spring" && isFiniteNumber2(value.damping) && isFiniteNumber2(value.mass) && isFiniteNumber2(value.stiffness) && (value.allowTail === undefined || value.allowTail === null || typeof value.allowTail === "boolean") && (value.durationRestThreshold === undefined || value.durationRestThreshold === null || isFiniteNumber2(value.durationRestThreshold)) && typeof value.overshootClamping === "boolean");
};
var normalizeKeyframeEasing = (easing) => {
  if (easing.type !== "spring") {
    return easing;
  }
  return {
    ...easing,
    allowTail: easing.allowTail ?? null,
    durationRestThreshold: easing.durationRestThreshold ?? null
  };
};
var parseEasingClipboardDataResult = (value) => {
  try {
    const parsed = JSON.parse(value);
    if (!isRecord2(parsed)) {
      return { status: "invalid" };
    }
    if (parsed.remotionClipboard !== "easing") {
      return { status: "invalid" };
    }
    if (parsed.version !== 1) {
      return {
        status: "unsupported-version",
        version: parsed.version
      };
    }
    if (parsed.type !== "easing" || !isKeyframeEasing(parsed.easing)) {
      return { status: "invalid" };
    }
    return {
      status: "valid",
      data: {
        type: "easing",
        version: 1,
        remotionClipboard: "easing",
        easing: normalizeKeyframeEasing(parsed.easing)
      }
    };
  } catch {
    return { status: "invalid" };
  }
};
var parseEasingClipboardData = (value) => {
  const result = parseEasingClipboardDataResult(value);
  if (result.status !== "valid") {
    return null;
  }
  return result.data;
};
// src/effect-catalog.ts
var getEffectDocumentationPath = (item) => {
  return `/docs/effects/${item.id.slice("effects-".length)}`;
};
var getEffectDocumentationLink = (item) => {
  return `https://www.remotion.dev${getEffectDocumentationPath(item)}`;
};
var getEffectPreviewSource = (item) => {
  if (item.id === "effects-evolve" || item.id === "effects-rain-fall") {
    return `https://remotion.media/docs/effects/${item.id}-preview-v2.png`;
  }
  return `https://remotion.media/docs/effects/${item.id}-preview.png`;
};
var getEffectPreviewAlt = (item) => {
  const effectName = item.id.slice("effects-".length).replaceAll("-", " ").replace(/^uv /, "UV ").replace(/^xy /, "XY ").replace(/^tv /, "TV ");
  return `${effectName} effect preview`;
};
var getEffectCatalogCategories = (items) => {
  const categories = [];
  for (const item of items) {
    const last = categories[categories.length - 1];
    if (last?.title === item.category) {
      categories[categories.length - 1] = {
        ...last,
        effects: [...last.effects, item]
      };
      continue;
    }
    categories.push({
      title: item.category,
      effects: [item]
    });
  }
  return categories;
};
var DEFAULT_LUT_CONTENT = `TITLE "Teal and orange"
LUT_3D_SIZE 2

0.02 0.05 0.08
0.95 0.12 0.06
0.02 0.8 0.4
1 0.9 0.18
0.08 0.18 0.9
0.95 0.2 0.75
0.15 0.88 0.92
1 0.92 0.78`;
var EFFECT_CATALOG = [
  {
    id: "effects-brightness",
    category: "Color",
    label: "brightness()",
    description: "Brightness adjustment effect",
    effect: {
      name: "brightness",
      importPath: "@remotion/effects/brightness",
      config: {}
    }
  },
  {
    id: "effects-contrast",
    category: "Color",
    label: "contrast()",
    description: "Contrast adjustment effect",
    effect: {
      name: "contrast",
      importPath: "@remotion/effects/contrast",
      config: {}
    }
  },
  {
    id: "effects-color-correction",
    category: "Color",
    label: "colorCorrection()",
    description: "Combined primary color adjustments",
    effect: {
      name: "colorCorrection",
      importPath: "@remotion/effects/color-correction",
      config: {}
    }
  },
  {
    id: "effects-color-key",
    category: "Color",
    label: "colorKey()",
    description: "Remove a key color (greenscreen)",
    effect: {
      name: "colorKey",
      importPath: "@remotion/effects/color-key",
      config: {
        similarity: 0.45
      }
    }
  },
  {
    id: "effects-curves",
    category: "Color",
    label: "curves()",
    description: "Adjust RGB or individual channels with a tonal curve",
    effect: {
      name: "curves",
      importPath: "@remotion/effects/curves",
      config: {}
    }
  },
  {
    id: "effects-duotone",
    category: "Color",
    label: "duotone()",
    description: "Two-color threshold effect",
    effect: {
      name: "duotone",
      importPath: "@remotion/effects/duotone",
      config: {}
    }
  },
  {
    id: "effects-exposure",
    category: "Color",
    label: "exposure()",
    description: "Stop-based exposure adjustment",
    effect: {
      name: "exposure",
      importPath: "@remotion/effects/exposure",
      config: {}
    }
  },
  {
    id: "effects-grayscale",
    category: "Color",
    label: "grayscale()",
    description: "Black-and-white effect",
    effect: {
      name: "grayscale",
      importPath: "@remotion/effects/grayscale",
      config: {}
    }
  },
  {
    id: "effects-hue",
    category: "Color",
    label: "hue()",
    description: "Hue rotation effect",
    effect: {
      name: "hue",
      importPath: "@remotion/effects/hue",
      config: {}
    }
  },
  {
    id: "effects-invert",
    category: "Color",
    label: "invert()",
    description: "Negative color effect",
    effect: {
      name: "invert",
      importPath: "@remotion/effects/invert",
      config: {}
    }
  },
  {
    id: "effects-levels",
    category: "Color",
    label: "levels()",
    description: "Black point, white point, and gamma",
    effect: {
      name: "levels",
      importPath: "@remotion/effects/levels",
      config: {}
    }
  },
  {
    id: "effects-lut",
    category: "Color",
    label: "lut()",
    description: "Apply an inline 3D Cube LUT",
    effect: {
      name: "lut",
      importPath: "@remotion/effects/lut",
      config: {
        content: DEFAULT_LUT_CONTENT
      }
    }
  },
  {
    id: "effects-saturation",
    category: "Color",
    label: "saturation()",
    description: "Saturation adjustment effect",
    effect: {
      name: "saturation",
      importPath: "@remotion/effects/saturation",
      config: {}
    }
  },
  {
    id: "effects-shadows-highlights",
    category: "Color",
    label: "shadowsHighlights()",
    description: "Recover dark and bright tonal regions",
    effect: {
      name: "shadowsHighlights",
      importPath: "@remotion/effects/shadows-highlights",
      config: {}
    }
  },
  {
    id: "effects-tint",
    category: "Color",
    label: "tint()",
    description: "Color tint effect",
    effect: {
      name: "tint",
      importPath: "@remotion/effects/tint",
      config: {
        color: "#1ec8ff"
      }
    }
  },
  {
    id: "effects-white-balance",
    category: "Color",
    label: "whiteBalance()",
    description: "Temperature and tint correction",
    effect: {
      name: "whiteBalance",
      importPath: "@remotion/effects/white-balance",
      config: {}
    }
  },
  {
    id: "effects-vibrance",
    category: "Color",
    label: "vibrance()",
    description: "Selective saturation adjustment",
    effect: {
      name: "vibrance",
      importPath: "@remotion/effects/vibrance",
      config: {}
    }
  },
  {
    id: "effects-linear-gradient",
    category: "Color",
    label: "linearGradient()",
    description: "Two-stop gradient effect",
    effect: {
      name: "linearGradient",
      importPath: "@remotion/effects/linear-gradient",
      config: {}
    }
  },
  {
    id: "effects-linear-gradient-tint",
    category: "Color",
    label: "linearGradientTint()",
    description: "Gradient tint effect",
    effect: {
      name: "linearGradientTint",
      importPath: "@remotion/effects/linear-gradient-tint",
      config: {}
    }
  },
  {
    id: "effects-thermal-vision",
    category: "Color",
    label: "thermalVision()",
    description: "Thermal heat-map color effect",
    effect: {
      name: "thermalVision",
      importPath: "@remotion/effects/thermal-vision",
      config: {}
    }
  },
  {
    id: "effects-blur",
    category: "Blur & Shadow",
    label: "blur()",
    description: "Gaussian blur effect",
    effect: {
      name: "blur",
      importPath: "@remotion/effects/blur",
      config: {
        radius: 40
      }
    }
  },
  {
    id: "effects-lens-blur",
    category: "Blur & Shadow",
    label: "lensBlur()",
    description: "Camera defocus with shaped bokeh",
    effect: {
      name: "lensBlur",
      importPath: "@remotion/effects/lens-blur",
      config: {}
    }
  },
  {
    id: "effects-linear-progressive-blur",
    category: "Blur & Shadow",
    label: "linearProgressiveBlur()",
    description: "Gradient-controlled blur effect",
    effect: {
      name: "linearProgressiveBlur",
      importPath: "@remotion/effects/linear-progressive-blur",
      config: {}
    }
  },
  {
    id: "effects-radial-progressive-blur",
    category: "Blur & Shadow",
    label: "radialProgressiveBlur()",
    description: "Ellipse-controlled blur effect",
    effect: {
      name: "radialProgressiveBlur",
      importPath: "@remotion/effects/radial-progressive-blur",
      config: {}
    }
  },
  {
    id: "effects-region-blur",
    category: "Blur & Shadow",
    label: "regionBlur()",
    description: "Blur a rectangular or rounded region",
    effect: {
      name: "regionBlur",
      importPath: "@remotion/effects/region-blur",
      config: {
        topLeft: [0.28, 0.18],
        bottomRight: [0.72, 0.82]
      }
    }
  },
  {
    id: "effects-zoom-blur",
    category: "Blur & Shadow",
    label: "zoomBlur()",
    description: "Radial zoom blur effect",
    effect: {
      name: "zoomBlur",
      importPath: "@remotion/effects/zoom-blur",
      config: {}
    }
  },
  {
    id: "effects-drop-shadow",
    category: "Blur & Shadow",
    label: "dropShadow()",
    description: "Blurred alpha shadow effect",
    effect: {
      name: "dropShadow",
      importPath: "@remotion/effects/drop-shadow",
      config: {}
    }
  },
  {
    id: "effects-long-shadows",
    category: "Blur & Shadow",
    label: "longShadows()",
    description: "Solid directional shadow or faux extrusion",
    effect: {
      name: "longShadows",
      importPath: "@remotion/effects/long-shadows",
      config: {}
    }
  },
  {
    id: "effects-glow",
    category: "Blur & Shadow",
    label: "glow()",
    description: "Soft halo effect",
    effect: {
      name: "glow",
      importPath: "@remotion/effects/glow",
      config: {}
    }
  },
  {
    id: "effects-outline",
    category: "Blur & Shadow",
    label: "outline()",
    description: "Solid alpha outline effect",
    effect: {
      name: "outline",
      importPath: "@remotion/effects/outline",
      config: {
        width: 12,
        color: "#00ffff"
      }
    }
  },
  {
    id: "effects-light-trail",
    category: "Blur & Shadow",
    label: "lightTrail()",
    description: "Directional light trail effect",
    effect: {
      name: "lightTrail",
      importPath: "@remotion/effects/light-trail",
      config: {}
    }
  },
  {
    id: "effects-evolve",
    category: "Reveal",
    label: "evolve()",
    description: "Directional reveal with angle and feathering",
    effect: {
      name: "evolve",
      importPath: "@remotion/effects/evolve",
      config: {
        progress: 0.5,
        direction: "angle",
        angle: 60,
        featherUnit: "pixels",
        featherPixels: 60
      }
    }
  },
  {
    id: "effects-tear",
    category: "Reveal",
    label: "tear()",
    description: "Progressive zigzag tear with outward rotation",
    effect: { name: "tear", importPath: "@remotion/effects/tear", config: {} }
  },
  {
    id: "effects-film-burn",
    category: "Reveal",
    label: "filmBurn()",
    description: "Irregular burn holes with charred and glowing edges",
    effect: {
      name: "filmBurn",
      importPath: "@remotion/effects/film-burn",
      config: {
        progress: 0.4,
        center: [0.5, 0.5],
        seed: 0
      }
    }
  },
  {
    id: "effects-radial-wipe",
    category: "Reveal",
    label: "radialWipe()",
    description: "Rotating wedge wipe around a center point",
    effect: {
      name: "radialWipe",
      importPath: "@remotion/effects/radial-wipe",
      config: {
        progress: 0.3,
        startAngle: 0,
        center: [0.5, 0.5],
        direction: "clockwise",
        feather: 8
      }
    }
  },
  {
    id: "effects-iris-wipe",
    category: "Reveal",
    label: "irisWipe()",
    description: "Polygon or star opening with feathered edges",
    effect: {
      name: "irisWipe",
      importPath: "@remotion/effects/iris-wipe",
      config: {
        center: [0.5, 0.5],
        points: 5,
        outerRadius: 300,
        useInnerRadius: true,
        innerRadius: 140,
        rotation: 0,
        feather: 12
      }
    }
  },
  {
    id: "effects-venetian-blinds",
    category: "Reveal",
    label: "venetianBlinds()",
    description: "Slatted reveal effect",
    effect: {
      name: "venetianBlinds",
      importPath: "@remotion/effects/venetian-blinds",
      config: {}
    }
  },
  {
    id: "effects-mirror",
    category: "Transform",
    label: "mirror()",
    description: "Mirror reflection effect",
    effect: {
      name: "mirror",
      importPath: "@remotion/effects/mirror",
      config: {}
    }
  },
  {
    id: "effects-scale",
    category: "Transform",
    label: "scale()",
    description: "Scale transform effect",
    effect: {
      name: "scale",
      importPath: "@remotion/effects/scale",
      config: {
        scale: 1
      }
    }
  },
  {
    id: "effects-tile",
    category: "Transform",
    label: "tile()",
    description: "Repeat the visible source bounds",
    effect: {
      name: "tile",
      importPath: "@remotion/effects/tile",
      config: {}
    }
  },
  {
    id: "effects-uv-translate",
    category: "Transform",
    label: "uvTranslate()",
    description: "UV-based translate effect",
    effect: {
      name: "uvTranslate",
      importPath: "@remotion/effects/translate",
      config: {}
    }
  },
  {
    id: "effects-xy-translate",
    category: "Transform",
    label: "xyTranslate()",
    description: "Pixel-based translate effect",
    effect: {
      name: "xyTranslate",
      importPath: "@remotion/effects/translate",
      config: {}
    }
  },
  {
    id: "effects-barrel-distortion",
    category: "Distort",
    label: "barrelDistortion()",
    description: "Barrel distortion effect",
    effect: {
      name: "barrelDistortion",
      importPath: "@remotion/effects/barrel-distortion",
      config: {}
    }
  },
  {
    id: "effects-chromatic-aberration",
    category: "Distort",
    label: "chromaticAberration()",
    description: "RGB channel split effect",
    effect: {
      name: "chromaticAberration",
      importPath: "@remotion/effects/chromatic-aberration",
      config: {}
    }
  },
  {
    id: "effects-fisheye",
    category: "Distort",
    label: "fisheye()",
    description: "Ultra-wide-angle lens effect",
    effect: {
      name: "fisheye",
      importPath: "@remotion/effects/fisheye",
      config: {}
    }
  },
  {
    id: "effects-corner-pin",
    category: "Distort",
    label: "cornerPin()",
    description: "Pin source corners to a quad",
    effect: {
      name: "cornerPin",
      importPath: "@remotion/effects/corner-pin",
      config: {
        topLeft: [0.08, 0.12],
        topRight: [0.92, 0.04],
        bottomRight: [0.86, 0.9],
        bottomLeft: [0.14, 0.96]
      }
    }
  },
  {
    id: "effects-page-turn",
    category: "Distort",
    label: "pageTurn()",
    description: "Curl a page with a shaded fold and paper back",
    effect: {
      name: "pageTurn",
      importPath: "@remotion/effects/page-turn",
      config: {}
    }
  },
  {
    id: "effects-sphere",
    category: "Distort",
    label: "sphere()",
    description: "Wrap content around a rotating sphere",
    effect: {
      name: "sphere",
      importPath: "@remotion/effects/sphere",
      config: {}
    }
  },
  {
    id: "effects-cylinder",
    category: "Distort",
    label: "cylinder()",
    description: "Wrap content around a rotating cylinder",
    effect: {
      name: "cylinder",
      importPath: "@remotion/effects/cylinder",
      config: {}
    }
  },
  {
    id: "effects-wave",
    category: "Distort",
    label: "wave()",
    description: "Sine wave distortion",
    effect: {
      name: "wave",
      importPath: "@remotion/effects/wave",
      config: {}
    }
  },
  {
    id: "effects-skew",
    category: "Distort",
    label: "skew()",
    description: "Skew the source on two axes",
    effect: {
      name: "skew",
      importPath: "@remotion/effects/skew",
      config: {}
    }
  },
  {
    id: "effects-turbulent-displace",
    category: "Distort",
    label: "turbulentDisplace()",
    description: "Evolving turbulent, bulge and twist distortion",
    effect: {
      name: "turbulentDisplace",
      importPath: "@remotion/effects/turbulent-displace",
      config: {}
    }
  },
  {
    id: "effects-burlap",
    category: "Stylize",
    label: "burlap()",
    description: "Procedural woven texture effect",
    effect: {
      name: "burlap",
      importPath: "@remotion/effects/burlap",
      config: {}
    }
  },
  {
    id: "effects-bevel-alpha",
    category: "Stylize",
    label: "bevelAlpha()",
    description: "Light and shade the edges of text and shapes",
    effect: {
      name: "bevelAlpha",
      importPath: "@remotion/effects/bevel-alpha",
      config: {}
    }
  },
  {
    id: "effects-emboss",
    category: "Stylize",
    label: "emboss()",
    description: "Procedural raised-line relief",
    effect: {
      name: "emboss",
      importPath: "@remotion/effects/emboss",
      config: {}
    }
  },
  {
    id: "effects-dot-grid",
    category: "Stylize",
    label: "dotGrid()",
    description: "Source-color dot mask effect",
    effect: {
      name: "dotGrid",
      importPath: "@remotion/effects/dot-grid",
      config: {}
    }
  },
  {
    id: "effects-halftone",
    category: "Stylize",
    label: "halftone()",
    description: "Source-image halftone effect",
    effect: {
      name: "halftone",
      importPath: "@remotion/effects/halftone",
      config: {}
    }
  },
  {
    id: "effects-noise",
    category: "Stylize",
    label: "noise()",
    description: "Procedural grain effect",
    effect: {
      name: "noise",
      importPath: "@remotion/effects/noise",
      config: {}
    }
  },
  {
    id: "effects-noise-displacement",
    category: "Stylize",
    label: "noiseDisplacement()",
    description: "Localized noisy displacement",
    effect: {
      name: "noiseDisplacement",
      importPath: "@remotion/effects/noise-displacement",
      config: {
        center: [0.6124309308853857, 0.5010527449123625],
        radius: 0.41,
        strength: 15.5,
        seed: 78,
        grainSize: 0.9,
        passes: 12,
        blur: 0,
        feather: 1,
        biasDirection: 313,
        biasAmount: 1
      }
    }
  },
  {
    id: "effects-paper",
    category: "Stylize",
    label: "paper()",
    description: "Procedural paper texture effect",
    effect: {
      name: "paper",
      importPath: "@remotion/effects/paper",
      config: {}
    }
  },
  {
    id: "effects-roughen-edges",
    category: "Stylize",
    label: "roughenEdges()",
    description: "Procedural alpha-edge roughening",
    effect: {
      name: "roughenEdges",
      importPath: "@remotion/effects/roughen-edges",
      config: {}
    }
  },
  {
    id: "effects-pattern",
    category: "Stylize",
    label: "pattern()",
    description: "Repeated source tile effect",
    effect: {
      name: "pattern",
      importPath: "@remotion/effects/pattern",
      config: {}
    }
  },
  {
    id: "effects-pixel-dissolve",
    category: "Stylize",
    label: "pixelDissolve()",
    description: "Pixelated dissolve effect",
    effect: {
      name: "pixelDissolve",
      importPath: "@remotion/effects/pixel-dissolve",
      config: {}
    }
  },
  {
    id: "effects-pixelate",
    category: "Stylize",
    label: "pixelate()",
    description: "Pixelation effect",
    effect: {
      name: "pixelate",
      importPath: "@remotion/effects/pixelate",
      config: {
        blockSize: 20
      }
    }
  },
  {
    id: "effects-linear-progressive-pixelate",
    category: "Stylize",
    label: "linearProgressivePixelate()",
    description: "Gradient-controlled pixelation",
    effect: {
      name: "linearProgressivePixelate",
      importPath: "@remotion/effects/linear-progressive-pixelate",
      config: {}
    }
  },
  {
    id: "effects-radial-progressive-pixelate",
    category: "Stylize",
    label: "radialProgressivePixelate()",
    description: "Ellipse-controlled pixelation",
    effect: {
      name: "radialProgressivePixelate",
      importPath: "@remotion/effects/radial-progressive-pixelate",
      config: {}
    }
  },
  {
    id: "effects-scanlines",
    category: "Stylize",
    label: "scanlines()",
    description: "Additive horizontal scanlines",
    effect: {
      name: "scanlines",
      importPath: "@remotion/effects/scanlines",
      config: {}
    }
  },
  {
    id: "effects-speckle",
    category: "Stylize",
    label: "speckle()",
    description: "Random alpha-hole effect",
    effect: {
      name: "speckle",
      importPath: "@remotion/effects/speckle",
      config: {}
    }
  },
  {
    id: "effects-shatter",
    category: "Stylize",
    label: "shatter()",
    description: "Break the source into tumbling fragments",
    effect: {
      name: "shatter",
      importPath: "@remotion/effects/shatter",
      config: {}
    }
  },
  {
    id: "effects-shine",
    category: "Stylize",
    label: "shine()",
    description: "Glossy light sweep effect",
    effect: {
      name: "shine",
      importPath: "@remotion/effects/shine",
      config: {}
    }
  },
  {
    id: "effects-shrinkwrap",
    category: "Stylize",
    label: "shrinkwrap()",
    description: "Procedural plastic wrap effect",
    effect: {
      name: "shrinkwrap",
      importPath: "@remotion/effects/shrinkwrap",
      config: {
        amount: 0.94,
        displacement: 13.5,
        highlightIntensity: 1.54,
        wrinkleDensity: 0.87,
        edgeTension: 0.58,
        phase: 0,
        seed: 12
      }
    }
  },
  {
    id: "effects-vignette",
    category: "Stylize",
    label: "vignette()",
    description: "Edge darkening or transparency effect",
    effect: {
      name: "vignette",
      importPath: "@remotion/effects/vignette",
      config: {}
    }
  },
  {
    id: "effects-rain-fall",
    category: "Generate",
    label: "rainFall()",
    description: "Falling rain with wind and depth",
    effect: {
      name: "rainFall",
      importPath: "@remotion/effects/rain-fall",
      config: {
        time: 1,
        drops: 1e5,
        size: 2,
        wind: 200,
        opacity: 0.5
      }
    }
  },
  {
    id: "effects-snow-fall",
    category: "Generate",
    label: "snowFall()",
    description: "Drifting snow with wind and depth",
    effect: {
      name: "snowFall",
      importPath: "@remotion/effects/snow-fall",
      config: {
        time: 1,
        flakes: 1500,
        size: 8
      }
    }
  },
  {
    id: "effects-fractal-noise",
    category: "Generate",
    label: "fractalNoise()",
    description: "Evolving fractal noise textures",
    effect: {
      name: "fractalNoise",
      importPath: "@remotion/effects/fractal-noise",
      config: {}
    }
  },
  {
    id: "effects-contour-lines",
    category: "Generate",
    label: "contourLines()",
    description: "Topographic line overlay effect",
    effect: {
      name: "contourLines",
      importPath: "@remotion/effects/contour-lines",
      config: {}
    }
  },
  {
    id: "effects-liquid-contours",
    category: "Generate",
    label: "liquidContours()",
    description: "Two-color liquid contour background",
    effect: {
      name: "liquidContours",
      importPath: "@remotion/effects/liquid-contours",
      config: {}
    }
  },
  {
    id: "effects-checkerboard",
    category: "Generate",
    label: "checkerboard()",
    description: "Checkerboard pattern effect",
    effect: {
      name: "checkerboard",
      importPath: "@remotion/effects/checkerboard",
      config: {}
    }
  },
  {
    id: "effects-flannel",
    category: "Generate",
    label: "flannel()",
    description: "Plaid woven fabric pattern",
    effect: {
      name: "flannel",
      importPath: "@remotion/effects/flannel",
      config: {}
    }
  },
  {
    id: "effects-halftone-linear-gradient",
    category: "Generate",
    label: "halftoneLinearGradient()",
    description: "Procedural dot gradient effect",
    effect: {
      name: "halftoneLinearGradient",
      importPath: "@remotion/effects/halftone-linear-gradient",
      config: {}
    }
  },
  {
    id: "effects-gridlines",
    category: "Generate",
    label: "gridlines()",
    description: "Procedural grid pattern effect",
    effect: {
      name: "gridlines",
      importPath: "@remotion/effects/gridlines",
      config: {}
    }
  },
  {
    id: "effects-white-noise",
    category: "Generate",
    label: "whiteNoise()",
    description: "Random grayscale noise layer",
    effect: {
      name: "whiteNoise",
      importPath: "@remotion/effects/white-noise",
      config: {}
    }
  },
  {
    id: "effects-tv-signal-off",
    category: "Generate",
    label: "tvSignalOff()",
    description: "TV color bars test pattern",
    effect: {
      name: "tvSignalOff",
      importPath: "@remotion/effects/tv-signal-off",
      config: {}
    }
  },
  {
    id: "effects-lines",
    category: "Generate",
    label: "lines()",
    description: "Alternating line pattern effect",
    effect: {
      name: "lines",
      importPath: "@remotion/effects/lines",
      config: {}
    }
  },
  {
    id: "effects-rings",
    category: "Generate",
    label: "rings()",
    description: "Concentric ring pattern effect",
    effect: {
      name: "rings",
      importPath: "@remotion/effects/rings",
      config: {}
    }
  },
  {
    id: "effects-waves",
    category: "Generate",
    label: "waves()",
    description: "Wavy band pattern effect",
    effect: {
      name: "waves",
      importPath: "@remotion/effects/waves",
      config: {}
    }
  },
  {
    id: "effects-zigzag",
    category: "Generate",
    label: "zigzag()",
    description: "Zig-zag band pattern effect",
    effect: {
      name: "zigzag",
      importPath: "@remotion/effects/zigzag",
      config: {}
    }
  },
  {
    id: "effects-light-leak",
    category: "Generate",
    label: "lightLeak()",
    description: "Light leak overlay effect",
    effect: {
      name: "lightLeak",
      importPath: "@remotion/effects/light-leak",
      config: {}
    }
  },
  {
    id: "effects-starburst",
    category: "Generate",
    label: "starburst()",
    description: "Starburst ray effect",
    effect: {
      name: "starburst",
      importPath: "@remotion/effects/starburst",
      config: {
        rays: 16,
        colors: ["#ff6600", "#ffff00"]
      }
    }
  }
];
// src/keyframe-interpolation-function.ts
var keyframeInterpolationFunctions = [
  "interpolate",
  "interpolateColors",
  "interpolatePaths"
];
var KEYFRAME_FIELD_TYPE_SUPPORT = {
  array: false,
  asset: false,
  boolean: true,
  "remotion-captions": false,
  color: true,
  enum: true,
  "font-family": false,
  "font-weight": true,
  hidden: true,
  number: true,
  "rotation-css": true,
  "rotation-degrees": true,
  scale: true,
  "svg-path": true,
  string: false,
  "text-content": false,
  "transform-origin": true,
  translate: true,
  "uv-coordinate": true
};
var KEYFRAME_FIELD_TYPE_INTERPOLATION = {
  array: "unsupported",
  asset: "unsupported",
  boolean: "interpolate",
  "remotion-captions": "unsupported",
  color: "interpolateColors",
  enum: "interpolate",
  "font-family": "unsupported",
  "font-weight": "interpolate",
  hidden: "infer",
  number: "infer",
  "rotation-css": "interpolate",
  "rotation-degrees": "infer",
  scale: "interpolate",
  "svg-path": "interpolatePaths",
  string: "unsupported",
  "text-content": "unsupported",
  "transform-origin": "interpolate",
  translate: "interpolate",
  "uv-coordinate": "infer"
};
var KEYFRAME_FIELD_TYPE_OUTPUT_TYPE = {
  array: null,
  asset: null,
  boolean: null,
  "remotion-captions": null,
  color: null,
  enum: null,
  "font-family": null,
  "font-weight": "font-weight",
  hidden: null,
  number: null,
  "rotation-css": "rotate",
  "rotation-degrees": null,
  scale: "scale",
  "svg-path": null,
  string: null,
  "text-content": null,
  "transform-origin": "transform-origin",
  translate: "translate",
  "uv-coordinate": null
};
var KEYFRAME_INTERPOLATION_EASING_SUPPORT = {
  interpolate: true,
  interpolateColors: true,
  interpolatePaths: true
};
var isKeyframeInterpolationFunction = (name) => {
  return keyframeInterpolationFunctions.includes(name);
};
var canEditEasingForInterpolationFunction = (interpolationFunction) => isKeyframeInterpolationFunction(interpolationFunction) && KEYFRAME_INTERPOLATION_EASING_SUPPORT[interpolationFunction];
var isInteractivitySchemaFieldKeyframable = (field) => {
  if (!field) {
    return true;
  }
  if (field.type === "enum") {
    return field.keyframable === true;
  }
  return KEYFRAME_FIELD_TYPE_SUPPORT[field.type] && field.keyframable !== false;
};
var findFieldInSchema = (schema, key) => {
  if (key in schema) {
    return schema[key];
  }
  for (const field of Object.values(schema)) {
    if (field.type !== "enum") {
      continue;
    }
    for (const variant of Object.values(field.variants)) {
      const found = findFieldInSchema(variant, key);
      if (found) {
        return found;
      }
    }
  }
  return;
};
var isSchemaFieldKeyframable = ({
  schema,
  key
}) => {
  const field = schema ? findFieldInSchema(schema, key) : undefined;
  return isInteractivitySchemaFieldKeyframable(field);
};
var isInteractivitySchemaFieldHoldOnly = (field) => {
  return field?.type === "boolean" && field.keyframable !== false || field?.type === "enum" && field.keyframable === true || field?.type === "number" && field.integer === true && field.keyframable !== false;
};
var isSchemaFieldHoldOnly = ({
  schema,
  key
}) => {
  return isInteractivitySchemaFieldHoldOnly(schema ? findFieldInSchema(schema, key) : undefined);
};
var getKeyframeInterpolationFunctionForSchemaField = ({
  schema,
  key
}) => {
  const field = schema ? findFieldInSchema(schema, key) : undefined;
  if (!field) {
    return null;
  }
  const strategy = KEYFRAME_FIELD_TYPE_INTERPOLATION[field.type];
  return strategy === "infer" || strategy === "unsupported" ? null : strategy;
};
var getKeyframeOutputTypeForSchemaField = ({
  schema,
  key
}) => {
  const field = schema ? findFieldInSchema(schema, key) : undefined;
  return field ? KEYFRAME_FIELD_TYPE_OUTPUT_TYPE[field.type] : null;
};
var getKeyframeInterpolationFunction = ({
  schema,
  key,
  staticValue,
  newValue
}) => {
  const schemaFunction = getKeyframeInterpolationFunctionForSchemaField({
    schema,
    key
  });
  if (schemaFunction) {
    return schemaFunction;
  }
  return typeof staticValue === "string" && typeof newValue === "string" ? "interpolateColors" : "interpolate";
};

// src/effect-clipboard-data.ts
var isRecord3 = (value) => {
  return typeof value === "object" && value !== null && !Array.isArray(value);
};
var extrapolateTypes = new Set(["extend", "identity", "clamp", "wrap"]);
var outputOptions = new Set(["linear", "perceptual-scale"]);
var isFiniteNumber3 = (value) => {
  return typeof value === "number" && Number.isFinite(value);
};
var isEasing = (value) => {
  return isRecord3(value) && (value.type === "linear" || value.type === "step1" || value.type === "bezier" && isFiniteNumber3(value.x1) && isFiniteNumber3(value.y1) && isFiniteNumber3(value.x2) && isFiniteNumber3(value.y2) || value.type === "spring" && isFiniteNumber3(value.damping) && isFiniteNumber3(value.mass) && isFiniteNumber3(value.stiffness) && (value.allowTail === undefined || value.allowTail === null || typeof value.allowTail === "boolean") && (value.durationRestThreshold === undefined || value.durationRestThreshold === null || isFiniteNumber3(value.durationRestThreshold)) && typeof value.overshootClamping === "boolean");
};
var normalizeEasing = (easing) => {
  if (easing.type !== "spring") {
    return easing;
  }
  return {
    ...easing,
    allowTail: easing.allowTail ?? null,
    durationRestThreshold: easing.durationRestThreshold ?? null
  };
};
var normalizeEffectClipboardParam = (param) => {
  if (param.type === "static") {
    return param;
  }
  return {
    ...param,
    easing: param.easing.map(normalizeEasing)
  };
};
var normalizeSnapshot = (snapshot) => {
  return {
    ...snapshot,
    params: Object.fromEntries(Object.entries(snapshot.params).map(([key, param]) => [
      key,
      normalizeEffectClipboardParam(param)
    ]))
  };
};
var isKeyframe = (value) => {
  return isRecord3(value) && isFiniteNumber3(value.frame) && "value" in value;
};
var isClamping = (value) => {
  return isRecord3(value) && typeof value.left === "string" && extrapolateTypes.has(value.left) && typeof value.right === "string" && extrapolateTypes.has(value.right);
};
var isEffectClipboardParam = (value) => {
  if (!isRecord3(value)) {
    return false;
  }
  if (value.type === "static") {
    return "value" in value;
  }
  if (value.type !== "keyframed") {
    return false;
  }
  const { posterize } = value;
  const { output } = value;
  const easingLength = Array.isArray(value.keyframes) && value.keyframes.length > 0 ? value.keyframes.length - 1 : null;
  return typeof value.interpolationFunction === "string" && isKeyframeInterpolationFunction(value.interpolationFunction) && Array.isArray(value.keyframes) && value.keyframes.length > 0 && value.keyframes.every(isKeyframe) && Array.isArray(value.easing) && value.easing.length === easingLength && value.easing.every(isEasing) && isClamping(value.clamping) && (output === undefined || value.interpolationFunction === "interpolate" && typeof output === "string" && outputOptions.has(output)) && (posterize === undefined || isFiniteNumber3(posterize) && posterize > 0);
};
var isEffectClipboardSnapshotV3 = (value) => {
  if (!isRecord3(value)) {
    return false;
  }
  return typeof value.callee === "string" && typeof value.importPath === "string" && isRecord3(value.params) && Object.values(value.params).every(isEffectClipboardParam);
};
var parseEffectClipboardDataResult = (value) => {
  try {
    const parsed = JSON.parse(value);
    if (!isRecord3(parsed)) {
      return { status: "invalid" };
    }
    if (parsed.remotionClipboard !== "effects") {
      return { status: "invalid" };
    }
    if (parsed.version !== 3) {
      return {
        status: "unsupported-version",
        version: parsed.version
      };
    }
    if (parsed.type !== "effects-additive" && parsed.type !== "effects-replacing") {
      return { status: "invalid" };
    }
    if (!Array.isArray(parsed.effects)) {
      return { status: "invalid" };
    }
    const effects = [];
    for (const effect of parsed.effects) {
      if (!isEffectClipboardSnapshotV3(effect)) {
        return { status: "invalid" };
      }
      effects.push(normalizeSnapshot(effect));
    }
    return {
      status: "valid",
      data: {
        type: parsed.type,
        version: 3,
        remotionClipboard: "effects",
        effects
      }
    };
  } catch {
    return { status: "invalid" };
  }
};
var parseEffectClipboardData = (value) => {
  const result = parseEffectClipboardDataResult(value);
  if (result.status !== "valid") {
    return null;
  }
  return result.data;
};
var parseEffectPropClipboardDataResult = (value) => {
  try {
    const parsed = JSON.parse(value);
    if (!isRecord3(parsed)) {
      return { status: "invalid" };
    }
    if (parsed.remotionClipboard !== "effect-prop") {
      return { status: "invalid" };
    }
    if (parsed.version !== 1) {
      return {
        status: "unsupported-version",
        version: parsed.version
      };
    }
    if (parsed.type !== "effect-prop") {
      return { status: "invalid" };
    }
    if (!isRecord3(parsed.effect)) {
      return { status: "invalid" };
    }
    if (typeof parsed.effect.callee !== "string" || typeof parsed.effect.importPath !== "string") {
      return { status: "invalid" };
    }
    if (typeof parsed.key !== "string") {
      return { status: "invalid" };
    }
    if (!isEffectClipboardParam(parsed.param)) {
      return { status: "invalid" };
    }
    return {
      status: "valid",
      data: {
        type: "effect-prop",
        version: 1,
        remotionClipboard: "effect-prop",
        effect: {
          callee: parsed.effect.callee,
          importPath: parsed.effect.importPath
        },
        key: parsed.key,
        param: normalizeEffectClipboardParam(parsed.param)
      }
    };
  } catch {
    return { status: "invalid" };
  }
};
var parseEffectPropClipboardData = (value) => {
  const result = parseEffectPropClipboardDataResult(value);
  if (result.status !== "valid") {
    return null;
  }
  return result.data;
};
// src/keyframe-clipboard-data.ts
var KEYFRAME_CLIPBOARD_FIELD_TYPE_SUPPORT = {
  array: false,
  asset: false,
  boolean: true,
  "remotion-captions": false,
  color: true,
  enum: false,
  "font-family": false,
  "font-weight": false,
  hidden: false,
  number: true,
  "rotation-css": true,
  "rotation-degrees": true,
  scale: true,
  "svg-path": true,
  string: false,
  "text-content": false,
  "transform-origin": true,
  translate: true,
  "uv-coordinate": true
};
var isRecord4 = (value) => {
  return typeof value === "object" && value !== null && !Array.isArray(value);
};
var isKeyframeClipboardFieldType = (value) => {
  return typeof value === "string" && Object.hasOwn(KEYFRAME_CLIPBOARD_FIELD_TYPE_SUPPORT, value) && KEYFRAME_CLIPBOARD_FIELD_TYPE_SUPPORT[value];
};
var isKeyframeClipboardEntry = (value) => {
  return isRecord4(value) && Number.isInteger(value.frameOffset) && Object.hasOwn(value, "value");
};
var areValidKeyframes = (value) => {
  if (!Array.isArray(value) || value.length === 0) {
    return false;
  }
  let previousOffset = -1;
  for (const keyframe of value) {
    if (!isKeyframeClipboardEntry(keyframe) || keyframe.frameOffset <= previousOffset) {
      return false;
    }
    previousOffset = keyframe.frameOffset;
  }
  return value[0]?.frameOffset === 0;
};
var isKeyframeClipboardField = (value) => {
  return isRecord4(value) && (value.type === "sequence" || value.type === "effect") && typeof value.fieldKey === "string";
};
var parseEasings = ({
  value,
  keyframeCount
}) => {
  if (Array.isArray(value) && value.length === Math.max(0, keyframeCount - 1) && value.every(isKeyframeEasing)) {
    return value.map(normalizeKeyframeEasing);
  }
  return null;
};
var parseKeyframeClipboardDataResult = (value) => {
  try {
    const parsed = JSON.parse(value);
    if (!isRecord4(parsed) || parsed.remotionClipboard !== "keyframe") {
      return { status: "invalid" };
    }
    if (parsed.version !== 1) {
      return { status: "unsupported-version", version: parsed.version };
    }
    const easing = parseEasings({
      value: parsed.easing,
      keyframeCount: Array.isArray(parsed.keyframes) ? parsed.keyframes.length : 0
    });
    if (parsed.type !== "keyframe" || !areValidKeyframes(parsed.keyframes) || easing === null || parsed.field !== null && !isKeyframeClipboardField(parsed.field) || parsed.fieldType !== null && !isKeyframeClipboardFieldType(parsed.fieldType)) {
      return { status: "invalid" };
    }
    return {
      status: "valid",
      data: {
        type: "keyframe",
        version: 1,
        remotionClipboard: "keyframe",
        fieldType: parsed.fieldType,
        field: parsed.field,
        keyframes: parsed.keyframes,
        easing
      }
    };
  } catch {
    return { status: "invalid" };
  }
};
var parseKeyframeClipboardData = (value) => {
  const result = parseKeyframeClipboardDataResult(value);
  return result.status === "valid" ? result.data : null;
};

// src/sequence-prop-clipboard-data.ts
var isRecord5 = (value) => {
  return typeof value === "object" && value !== null && !Array.isArray(value);
};
var parseSequencePropClipboardDataResult = (value) => {
  try {
    const parsed = JSON.parse(value);
    if (!isRecord5(parsed) || parsed.remotionClipboard !== "sequence-prop") {
      return { status: "invalid" };
    }
    if (parsed.version !== 1) {
      return { status: "unsupported-version", version: parsed.version };
    }
    if (parsed.type !== "sequence-prop" || typeof parsed.key !== "string" || !isKeyframeClipboardFieldType(parsed.fieldType) || !isEffectClipboardParam(parsed.param)) {
      return { status: "invalid" };
    }
    return {
      status: "valid",
      data: {
        type: "sequence-prop",
        version: 1,
        remotionClipboard: "sequence-prop",
        key: parsed.key,
        fieldType: parsed.fieldType,
        param: normalizeEffectClipboardParam(parsed.param)
      }
    };
  } catch {
    return { status: "invalid" };
  }
};
var parseSequencePropClipboardData = (value) => {
  const result = parseSequencePropClipboardDataResult(value);
  return result.status === "valid" ? result.data : null;
};
// src/format-bytes.ts
var BYTE_UNITS = ["B", "kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
var BIBYTE_UNITS = [
  "B",
  "kiB",
  "MiB",
  "GiB",
  "TiB",
  "PiB",
  "EiB",
  "ZiB",
  "YiB"
];
var BIT_UNITS = [
  "b",
  "kbit",
  "Mbit",
  "Gbit",
  "Tbit",
  "Pbit",
  "Ebit",
  "Zbit",
  "Ybit"
];
var BIBIT_UNITS = [
  "b",
  "kibit",
  "Mibit",
  "Gibit",
  "Tibit",
  "Pibit",
  "Eibit",
  "Zibit",
  "Yibit"
];
var toLocaleString = (number, locale, options) => {
  if (typeof locale === "string" || Array.isArray(locale)) {
    return number.toLocaleString(locale, options);
  }
  if (locale === true || options !== undefined) {
    return number.toLocaleString(undefined, options);
  }
  return String(number);
};
var formatBytes = (number, options = {
  locale: "en-US",
  signed: false,
  maximumFractionDigits: 1
}) => {
  if (!Number.isFinite(number)) {
    throw new TypeError(`Expected a finite number, got ${typeof number}: ${number}`);
  }
  options = { bits: false, binary: false, ...options };
  const UNITS = options.bits ? options.binary ? BIBIT_UNITS : BIT_UNITS : options.binary ? BIBYTE_UNITS : BYTE_UNITS;
  if (options.signed && number === 0) {
    return `0 $ {
            UNITS[0]
        }`;
  }
  const isNegative = number < 0;
  const prefix = isNegative ? "-" : options.signed ? "+" : "";
  if (isNegative) {
    number = -number;
  }
  let localeOptions;
  if (options.minimumFractionDigits !== undefined) {
    localeOptions = {
      minimumFractionDigits: options.minimumFractionDigits
    };
  }
  if (options.maximumFractionDigits !== undefined) {
    localeOptions = {
      maximumFractionDigits: options.maximumFractionDigits,
      ...localeOptions
    };
  }
  if (number < 1) {
    const numString = toLocaleString(number, options.locale, localeOptions);
    return prefix + numString + " " + UNITS[0];
  }
  const exponent = Math.min(Math.floor(options.binary ? Math.log(number) / Math.log(1024) : Math.log10(number) / 3), UNITS.length - 1);
  number /= (options.binary ? 1024 : 1000) ** exponent;
  const numberString = toLocaleString(Number(number), options.locale, localeOptions);
  const unit = UNITS[exponent];
  return prefix + numberString + " " + unit;
};
// src/get-all-keys.ts
import { Internals as Internals2 } from "remotion";
var getAllSchemaKeys = (schema) => {
  return Object.keys(Internals2.getFlatSchemaWithAllKeys(schema));
};
var getAssetSchemaKeys = (schema) => {
  return Object.entries(Internals2.getFlatSchemaWithAllKeys(schema)).filter(([, field]) => field?.type === "asset").map(([key]) => key);
};
// src/get-default-out-name.ts
var hasFileExtension = (location) => {
  const lastSegment = location.split("/").pop() ?? location;
  return lastSegment.includes(".");
};
var getDefaultOutLocation = ({
  compositionName,
  defaultExtension,
  type,
  compositionDefaultOutName,
  outputLocation
}) => {
  if (outputLocation && hasFileExtension(outputLocation)) {
    return outputLocation;
  }
  const base = outputLocation ?? "out";
  const dir = base.endsWith("/") ? base : `${base}/`;
  const nameToUse = compositionDefaultOutName ?? compositionName;
  if (type === "sequence") {
    return `${dir}${nameToUse}`;
  }
  return `${dir}${nameToUse}.${defaultExtension}`;
};
// src/get-location-from-build-error.ts
import { NoReactInternals } from "remotion/no-react";
var getLocationFromBuildError = (err) => {
  if (!err.stack) {
    return null;
  }
  const stack = stripAnsi(err.stack);
  if (!/^Error:\s*(?:×\s+)?Module build failed/.test(stack) && !stack.startsWith("Error: Cannot find module")) {
    return null;
  }
  const split = stack.split(`
`);
  for (let i = 0;i < split.length; i++) {
    const location = split[i].match(/\[(.+):(\d+):(\d+)\]/);
    if (!location) {
      continue;
    }
    const diagnostic = split.slice(0, i).reverse().map((line) => line.match(/^[\s│├╰─▶]*(?:[×x]\s+)+(.+)$/)?.[1]).find(NoReactInternals.truthy);
    if (diagnostic) {
      return {
        fileName: location[1],
        lineNumber: Number(location[2]),
        columnNumber: Number(location[3]),
        message: diagnostic
      };
    }
  }
  return split.map((s) => {
    if (s.startsWith("Error")) {
      return null;
    }
    const matchWebpackOrEsbuild = s.match(/(.*):([0-9]+):([0-9]+): (.*)/);
    if (matchWebpackOrEsbuild) {
      return {
        fileName: matchWebpackOrEsbuild[1],
        lineNumber: Number(matchWebpackOrEsbuild[2]),
        columnNumber: Number(matchWebpackOrEsbuild[3]),
        message: matchWebpackOrEsbuild[4]
      };
    }
    const matchMissingModule = s.match(/\s+at(.*)\s\((.*)\)/);
    if (!matchMissingModule) {
      return null;
    }
    if (s.includes("webpackMissingModule") || s.includes("__rspack_missing_module")) {
      return null;
    }
    const [, filename] = matchMissingModule;
    return {
      columnNumber: 0,
      lineNumber: 1,
      message: split[0],
      fileName: filename.trim()
    };
  }).filter(NoReactInternals.truthy)[0] ?? null;
};
// src/get-project-name.ts
var getProjectName = ({
  gitSource,
  resolvedRemotionRoot,
  basename
}) => {
  if (!gitSource) {
    return basename(resolvedRemotionRoot);
  }
  if (gitSource.relativeFromGitRoot.trim()) {
    return basename(gitSource.relativeFromGitRoot.trim());
  }
  return gitSource.name;
};
// src/get-preferred-app.ts
var preferredFallbackEditorIds = [
  "zed",
  "vscode",
  "cursor"
];
var getPreferredApp = ({
  installedApps,
  configuredId,
  runningIds,
  recentlyUsedIds,
  fallbackIds
}) => {
  const configuredApp = installedApps.find((app) => app.id === configuredId);
  if (configuredApp) {
    return configuredApp;
  }
  return [...installedApps].sort((a, b) => {
    const runningDifference = Number(runningIds.includes(b.id)) - Number(runningIds.includes(a.id));
    if (runningDifference !== 0) {
      return runningDifference;
    }
    for (const ids of [recentlyUsedIds, fallbackIds]) {
      const aIndex = ids.indexOf(a.id);
      const bIndex = ids.indexOf(b.id);
      const difference = (aIndex === -1 ? ids.length : aIndex) - (bIndex === -1 ? ids.length : bIndex);
      if (difference !== 0) {
        return difference;
      }
    }
    return a.nameWithType.localeCompare(b.nameWithType);
  }).at(0) ?? null;
};
// src/hot-middleware.ts
var hotMiddlewareOptions = {
  timeout: 20 * 1000,
  reload: true,
  warn: true
};
// src/keyframe-easing-presets.ts
var LINEAR_KEYFRAME_EASING = { type: "linear" };
var HOLD_KEYFRAME_EASING = { type: "step1" };
var EASE_KEYFRAME_EASING = {
  type: "bezier",
  x1: 0.42,
  y1: 0,
  x2: 1,
  y2: 1
};
var QUAD_KEYFRAME_EASING = {
  type: "bezier",
  x1: 1 / 3,
  y1: 0,
  x2: 2 / 3,
  y2: 1 / 3
};
var CUBIC_KEYFRAME_EASING = {
  type: "bezier",
  x1: 1 / 3,
  y1: 0,
  x2: 2 / 3,
  y2: 0
};
var getBackKeyframeEasing = (s = 1.70158) => ({
  type: "bezier",
  x1: 1 / 3,
  y1: 0,
  x2: 2 / 3,
  y2: -s / 3
});
var getPolyKeyframeEasing = (n) => {
  if (n === 1) {
    return LINEAR_KEYFRAME_EASING;
  }
  if (n === 2) {
    return QUAD_KEYFRAME_EASING;
  }
  if (n === 3) {
    return CUBIC_KEYFRAME_EASING;
  }
  return null;
};
var getOutKeyframeEasing = (easing) => {
  if (easing.type === "linear") {
    return LINEAR_KEYFRAME_EASING;
  }
  if (easing.type !== "bezier") {
    return null;
  }
  return {
    type: "bezier",
    x1: 1 - easing.x2,
    y1: 1 - easing.y2,
    x2: 1 - easing.x1,
    y2: 1 - easing.y1
  };
};
var KEYFRAME_EASING_PRESETS = [
  {
    id: "ease-in",
    label: "Ease in",
    easing: { type: "bezier", x1: 0.42, y1: 0, x2: 1, y2: 1 }
  },
  {
    id: "ease-out",
    label: "Ease out",
    easing: { type: "bezier", x1: 0, y1: 0, x2: 0.58, y2: 1 }
  },
  {
    id: "ease-in-out",
    label: "Ease in-out",
    easing: { type: "bezier", x1: 0.42, y1: 0, x2: 0.58, y2: 1 }
  },
  {
    id: "hold",
    label: "Hold",
    easing: HOLD_KEYFRAME_EASING
  },
  {
    id: "tail-spring",
    label: "Tail spring",
    easing: {
      type: "spring",
      allowTail: true,
      damping: 200,
      durationRestThreshold: 0.02,
      mass: 1,
      overshootClamping: false,
      stiffness: 100
    }
  },
  {
    id: "spring",
    label: "Spring",
    easing: {
      type: "spring",
      allowTail: true,
      damping: 10,
      durationRestThreshold: 0.02,
      mass: 1,
      overshootClamping: false,
      stiffness: 100
    }
  },
  {
    id: "bouncy-spring",
    label: "Bouncy spring",
    easing: {
      type: "spring",
      allowTail: true,
      damping: 5,
      durationRestThreshold: 0.02,
      mass: 1,
      overshootClamping: false,
      stiffness: 120
    }
  }
];
// src/max-timeline-tracks.ts
var DEFAULT_TIMELINE_TRACKS = 90;
// src/package-info.ts
import { VERSION } from "remotion";

// src/release-package-policy.ts
var packagesRemovedInV5 = [
  "@remotion/light-leaks",
  "@remotion/media-parser",
  "@remotion/starburst",
  "@remotion/webcodecs",
  "@remotion/whisper-web"
];
var packagesRemovedInV5Set = new Set(packagesRemovedInV5);
var shouldReleasePackage = ({
  packageName,
  releaseVersion
}) => {
  const majorVersion = Number.parseInt(releaseVersion.split(".")[0], 10);
  if (!Number.isInteger(majorVersion)) {
    throw new Error(`Invalid release version: ${releaseVersion}`);
  }
  return majorVersion < 5 || !packagesRemovedInV5Set.has(packageName);
};

// src/package-info.ts
var allPackages = [
  "svg-3d-engine",
  "animation-utils",
  "animated-emoji",
  "astro-example",
  "babel-loader",
  "bugs",
  "brand",
  "bundler",
  "browser-bundler",
  "browser-studio",
  "sdk",
  "claude-code-plugin",
  "cli",
  "cloudrun",
  "agent-plugin",
  "kimi-code-plugin",
  "compositor-darwin-arm64",
  "compositor-darwin-x64",
  "compositor-linux-arm64-gnu",
  "compositor-linux-arm64-musl",
  "compositor-linux-x64-gnu",
  "compositor-linux-x64-musl",
  "compositor-win32-x64-msvc",
  "core",
  "create-video",
  "discord-poster",
  "docusaurus-plugin",
  "docs",
  "enable-scss",
  "eslint-config",
  "eslint-config-flat",
  "eslint-config-internal",
  "eslint-plugin",
  "example-without-zod",
  "example",
  "fonts",
  "gif",
  "google-fonts",
  "gsap",
  "install-whisper-cpp",
  "it-tests",
  "react18-tests",
  "lambda-go-example",
  "lambda-go",
  "lambda-php",
  "lambda-ruby",
  "lambda-python",
  "lambda",
  "lambda-client",
  "layout-utils",
  "rounded-text-box",
  "licensing",
  "lottie",
  "media-utils",
  "motion-blur",
  "noise",
  "paths",
  "player-a11y",
  "player-example",
  "player",
  "preload",
  "renderer",
  "rive",
  "shapes",
  "skia",
  "promo-pages",
  "streaming",
  "skills",
  "skills-evals",
  "codemods",
  "studio-server",
  "studio-shared",
  "studio",
  "tailwind",
  "tailwind-v4",
  "timeline-utils",
  "test-utils",
  "three",
  "transitions",
  "media-parser",
  "zod-types",
  "zod-types-v3",
  "webcodecs",
  "convert",
  "captions",
  "openai-whisper",
  "elevenlabs",
  "compositor",
  "example-videos",
  "whisper-web",
  "whisper-webgpu",
  "video-matting",
  "media",
  "remotion-media",
  "web-renderer",
  "design",
  "studio-protocol",
  "light-leaks",
  "mac-cursors",
  "maptiler",
  "rough-notation",
  "starburst",
  "vercel",
  "sfx",
  "effects"
];
var packages = allPackages.filter((pkg) => shouldReleasePackage({
  packageName: pkg === "core" ? "remotion" : `@remotion/${pkg}`,
  releaseVersion: VERSION
}));
var extraPackages = [
  {
    name: "@huggingface/transformers",
    version: "4.2.0",
    description: "Machine learning models in the browser",
    docsUrl: "https://huggingface.co/docs/transformers.js",
    versionDocsUrl: "https://www.remotion.dev/docs/whisper-webgpu"
  },
  {
    name: "mediabunny",
    version: "1.64.0",
    description: "Multimedia library used by Remotion",
    docsUrl: "https://www.remotion.dev/docs/mediabunny",
    versionDocsUrl: "https://www.remotion.dev/docs/mediabunny/version"
  },
  {
    name: "@mediabunny/ac3",
    version: "1.64.0",
    description: "AC-3 and E-AC-3 audio codec support for Mediabunny",
    docsUrl: "https://www.remotion.dev/docs/mediabunny/formats#ac-3-and-e-ac-3",
    versionDocsUrl: "https://www.remotion.dev/docs/mediabunny/formats#ac-3-and-e-ac-3"
  },
  {
    name: "@mediabunny/dts",
    version: "1.64.0",
    description: "DTS audio codec support for Mediabunny",
    docsUrl: "https://www.remotion.dev/docs/mediabunny/formats#dts",
    versionDocsUrl: "https://www.remotion.dev/docs/mediabunny/formats#dts"
  },
  {
    name: "@mediabunny/prores",
    version: "1.64.0",
    description: "Apple ProRes decoder support for Mediabunny",
    docsUrl: "https://www.remotion.dev/docs/mediabunny/formats",
    versionDocsUrl: "https://www.remotion.dev/docs/mediabunny/formats"
  },
  {
    name: "zod",
    version: "4.6.7",
    description: "TypeScript-first schema validation",
    docsUrl: "https://zod.dev",
    versionDocsUrl: "https://www.remotion.dev/docs/zod-types/"
  }
];
var descriptions = {
  compositor: "Rust binary for Remotion",
  player: "React component for embedding a Remotion preview into your app",
  cloudrun: "Render Remotion videos on Google Cloud Run",
  "claude-code-plugin": null,
  "agent-plugin": null,
  "kimi-code-plugin": null,
  renderer: "Render Remotion videos using Node.js or Bun",
  cli: "Control Remotion features using the `npx remotion` command",
  core: "Make videos programmatically",
  lambda: "Render Remotion videos on AWS Lambda",
  bundler: "Bundle Remotion compositions using Webpack",
  "browser-bundler": "Compile virtual Remotion projects in the browser",
  "browser-studio": "Run Remotion Studio in the browser",
  sdk: "Experimental primitives for building Remotion authoring interfaces",
  codemods: "Codemods for Remotion projects",
  "studio-server": "Run a Remotion Studio with a server backend",
  "install-whisper-cpp": "Helpers for installing and using Whisper.cpp",
  "whisper-web": "Helpers for using Whisper.cpp in browser using WASM",
  "whisper-webgpu": "Transcribe audio in the browser using Whisper and WebGPU",
  "video-matting": "Separate a video into a base layer and transparent foreground in the browser",
  "google-fonts": "Use Google Fonts in Remotion",
  gsap: "Use GSAP timelines in Remotion",
  "media-utils": "Utilities for working with media files",
  lottie: "Include Lottie animations in Remotion",
  licensing: "Manage your Remotion.pro license",
  "layout-utils": "Utilities for working with layouts",
  "rounded-text-box": "Create a TikTok-like multiline text box SVG path with rounded corners",
  noise: "Noise generation functions",
  "motion-blur": "Motion blur effect for Remotion",
  preload: "Preloads assets for use in Remotion",
  shapes: "Generate SVG shapes",
  "zod-types": "Zod types for Remotion",
  "zod-types-v3": "Zod 3.22.3 types for Remotion",
  gif: "Embed GIFs in a Remotion video",
  "eslint-plugin": "Rules for writing Remotion code",
  "eslint-config": "Default configuration for Remotion templates (ESLint <= 8)",
  "eslint-config-flat": "Default configuration for Remotion templates (ESLint >= 9)",
  "compositor-linux-x64-gnu": "Linux x64 binary for the Remotion Rust code",
  "compositor-linux-x64-musl": "Linux x64 binary for the Remotion Rust code",
  "compositor-darwin-x64": "MacOS x64 binary for the Remotion Rust code",
  "compositor-darwin-arm64": "MacOS Apple Silicon binary for the Remotion Rust code",
  "compositor-linux-arm64-gnu": "Linux ARM64 binary for the Remotion Rust code",
  "compositor-linux-arm64-musl": "Linux ARM64 binary for the Remotion Rust code",
  "babel-loader": "Babel loader for Remotion",
  fonts: "Helpers for loading local fonts into Remotion",
  transitions: "Library for creating transitions in Remotion",
  "enable-scss": "Enable SCSS support in Remotion",
  "create-video": "Create a new Remotion project",
  "studio-shared": "Internal package for shared objects between the Studio backend and frontend",
  "timeline-utils": "Internal utilities for rendering Remotion timelines",
  tailwind: "Enable TailwindCSS support in Remotion (TailwindCSS v3)",
  "tailwind-v4": "Enable TailwindCSS support in Remotion (TailwindCSS v4)",
  streaming: "Utilities for streaming data between programs",
  "media-parser": "A pure JavaScript library for parsing video files",
  rive: "Embed Rive animations in a Remotion video",
  paths: "Utilities for working with SVG paths",
  studio: "APIs for interacting with the Remotion Studio",
  skia: "Include React Native Skia components in a Remotion video",
  three: "Include React Three Fiber components in a Remotion video",
  "astro-example": null,
  "lambda-go-example": null,
  "compositor-win32-x64-msvc": null,
  "animation-utils": "Helpers for animating CSS properties",
  "test-utils": null,
  "example-without-zod": null,
  "lambda-go": null,
  example: null,
  "lambda-php": null,
  "lambda-client": null,
  bugs: null,
  brand: null,
  docs: null,
  "it-tests": null,
  "react18-tests": null,
  "lambda-python": null,
  "lambda-ruby": null,
  "player-example": null,
  skills: null,
  "skills-evals": null,
  "discord-poster": null,
  "docusaurus-plugin": null,
  "animated-emoji": "Google Fonts Animated Emojis as Remotion components",
  webcodecs: "Media conversion in the browser",
  convert: "Video conversion tool - convert.remotion.dev",
  captions: "Primitives for dealing with captions",
  "openai-whisper": "Work with the output of the OpenAI Whisper API",
  elevenlabs: "Work with the output of the ElevenLabs API",
  "eslint-config-internal": "ESLint condig for Remotion's internal packages",
  "example-videos": null,
  "promo-pages": null,
  "svg-3d-engine": "3D SVG extrusion effects",
  media: "Experimental WebCodecs-based media tags",
  "remotion-media": null,
  "web-renderer": "Render videos in the browser",
  design: "Design system",
  "studio-protocol": "Create Element payloads and request installation into Remotion Studio",
  "light-leaks": "Light leak effects for Remotion",
  "mac-cursors": "macOS cursor components for Remotion",
  maptiler: "MapTiler components for Remotion",
  "rough-notation": "Rough annotation primitives for Remotion",
  "player-a11y": "Internal accessibility wrapper around @remotion/player",
  starburst: "Starburst ray effect for Remotion",
  vercel: "Render Remotion videos on Vercel Sandbox",
  sfx: "Sound effect library",
  effects: "Effects that can be applied to Remotion-based canvas components"
};
var installableMap = {
  "svg-3d-engine": false,
  "animation-utils": true,
  "animated-emoji": true,
  "astro-example": false,
  "babel-loader": false,
  bugs: false,
  brand: false,
  bundler: false,
  "browser-bundler": false,
  "browser-studio": false,
  sdk: false,
  cli: false,
  cloudrun: true,
  "claude-code-plugin": false,
  "agent-plugin": false,
  "kimi-code-plugin": false,
  "lambda-client": false,
  "compositor-darwin-arm64": false,
  "compositor-darwin-x64": false,
  "compositor-linux-arm64-gnu": false,
  "compositor-linux-arm64-musl": false,
  "compositor-linux-x64-gnu": false,
  "compositor-linux-x64-musl": false,
  "compositor-win32-x64-msvc": false,
  core: false,
  "create-video": false,
  "discord-poster": false,
  "docusaurus-plugin": false,
  docs: false,
  "enable-scss": true,
  "eslint-config": false,
  "eslint-config-flat": false,
  "eslint-config-internal": false,
  "eslint-plugin": false,
  "example-without-zod": false,
  example: false,
  fonts: true,
  gif: true,
  "google-fonts": true,
  gsap: true,
  "install-whisper-cpp": true,
  "whisper-web": shouldReleasePackage({
    packageName: "@remotion/whisper-web",
    releaseVersion: VERSION
  }),
  "whisper-webgpu": true,
  "video-matting": true,
  "it-tests": false,
  "react18-tests": false,
  "lambda-go-example": false,
  "lambda-go": false,
  "lambda-php": false,
  "lambda-ruby": false,
  "lambda-python": false,
  lambda: true,
  "layout-utils": true,
  "rounded-text-box": true,
  licensing: true,
  lottie: true,
  "media-utils": true,
  "motion-blur": true,
  noise: true,
  paths: true,
  "player-example": false,
  "player-a11y": false,
  player: true,
  preload: true,
  renderer: true,
  rive: true,
  shapes: true,
  skia: true,
  skills: false,
  "skills-evals": false,
  "promo-pages": false,
  streaming: false,
  codemods: false,
  "studio-server": false,
  "studio-shared": false,
  studio: true,
  tailwind: true,
  "tailwind-v4": true,
  "timeline-utils": false,
  "test-utils": false,
  three: true,
  transitions: true,
  "media-parser": shouldReleasePackage({
    packageName: "@remotion/media-parser",
    releaseVersion: VERSION
  }),
  "zod-types": true,
  "zod-types-v3": true,
  webcodecs: shouldReleasePackage({
    packageName: "@remotion/webcodecs",
    releaseVersion: VERSION
  }),
  convert: false,
  captions: true,
  "openai-whisper": true,
  elevenlabs: true,
  compositor: false,
  "example-videos": false,
  media: true,
  "remotion-media": false,
  "web-renderer": false,
  design: false,
  "studio-protocol": true,
  "light-leaks": shouldReleasePackage({
    packageName: "@remotion/light-leaks",
    releaseVersion: VERSION
  }),
  "mac-cursors": shouldReleasePackage({
    packageName: "@remotion/mac-cursors",
    releaseVersion: VERSION
  }),
  maptiler: false,
  "rough-notation": true,
  starburst: shouldReleasePackage({
    packageName: "@remotion/starburst",
    releaseVersion: VERSION
  }),
  vercel: true,
  sfx: true,
  effects: true
};
var apiDocs = {
  player: "https://www.remotion.dev/docs/player",
  cloudrun: "https://www.remotion.dev/docs/cloudrun",
  "claude-code-plugin": null,
  "agent-plugin": null,
  "kimi-code-plugin": null,
  renderer: "https://www.remotion.dev/docs/renderer",
  cli: "https://www.remotion.dev/docs/cli",
  core: "https://www.remotion.dev/docs/remotion",
  lambda: "https://www.remotion.dev/docs/lambda",
  bundler: "https://www.remotion.dev/docs/bundler",
  "browser-bundler": "https://www.remotion.dev/docs/browser-bundler",
  "browser-studio": null,
  sdk: "https://www.remotion.dev/docs/sdk",
  codemods: "https://www.remotion.dev/docs/codemods",
  "lambda-client": null,
  "studio-server": null,
  "install-whisper-cpp": "https://www.remotion.dev/docs/install-whisper-cpp",
  "whisper-web": "https://www.remotion.dev/docs/whisper-web",
  "whisper-webgpu": "https://www.remotion.dev/docs/whisper-webgpu",
  "video-matting": "https://www.remotion.dev/docs/video-matting",
  "google-fonts": "https://www.remotion.dev/docs/google-fonts",
  gsap: "https://www.remotion.dev/docs/gsap",
  "media-utils": "https://www.remotion.dev/docs/media-utils",
  lottie: "https://www.remotion.dev/docs/lottie",
  licensing: "https://www.remotion.dev/docs/licensing",
  "layout-utils": "https://www.remotion.dev/docs/layout-utils",
  "rounded-text-box": "https://www.remotion.dev/docs/rounded-text-box",
  noise: "https://www.remotion.dev/docs/noise",
  "motion-blur": "https://www.remotion.dev/docs/motion-blur/api",
  preload: "https://www.remotion.dev/docs/preload",
  shapes: "https://www.remotion.dev/docs/shapes",
  "zod-types": "https://www.remotion.dev/docs/zod-types",
  "zod-types-v3": "https://www.remotion.dev/docs/zod-types/v3",
  gif: "https://www.remotion.dev/docs/gif",
  "eslint-plugin": "https://www.remotion.dev/docs/brownfield#install-the-eslint-plugin",
  "eslint-config": "https://www.remotion.dev/docs/brownfield#install-the-eslint-plugin",
  "eslint-config-flat": "https://www.remotion.dev/docs/brownfield#install-the-eslint-plugin",
  "compositor-linux-x64-gnu": null,
  "compositor-linux-x64-musl": null,
  "compositor-darwin-x64": null,
  "discord-poster": null,
  "docusaurus-plugin": null,
  "animation-utils": "https://www.remotion.dev/docs/animation-utils/",
  "example-without-zod": null,
  "lambda-go": null,
  example: null,
  "lambda-php": null,
  bugs: null,
  brand: null,
  docs: null,
  "it-tests": null,
  "react18-tests": null,
  "lambda-python": null,
  "lambda-ruby": "https://www.remotion.dev/docs/lambda/ruby",
  "player-example": null,
  "player-a11y": null,
  "astro-example": null,
  "lambda-go-example": null,
  "test-utils": null,
  "babel-loader": "https://www.remotion.dev/docs/legacy-babel",
  "compositor-darwin-arm64": null,
  "compositor-linux-arm64-gnu": null,
  "compositor-linux-arm64-musl": null,
  "compositor-win32-x64-msvc": null,
  "enable-scss": "https://www.remotion.dev/docs/enable-scss/overview",
  "create-video": "https://remotion.dev/templates",
  "studio-shared": null,
  "media-parser": "https://www.remotion.dev/docs/media-parser",
  fonts: "https://www.remotion.dev/docs/fonts-api",
  paths: "https://www.remotion.dev/paths",
  rive: "https://www.remotion.dev/docs/rive",
  tailwind: "https://www.remotion.dev/docs/tailwind/tailwind",
  "tailwind-v4": "https://www.remotion.dev/docs/tailwind/tailwind",
  skia: "https://www.remotion.dev/docs/skia",
  three: "https://www.remotion.dev/docs/three",
  streaming: null,
  skills: null,
  "skills-evals": null,
  studio: "https://www.remotion.dev/docs/studio/api",
  "timeline-utils": null,
  transitions: "https://www.remotion.dev/transitions",
  "animated-emoji": "https://www.remotion.dev/docs/animated-emoji",
  webcodecs: "https://remotion.dev/webcodecs",
  convert: "https://convert.remotion.dev",
  captions: "https://remotion.dev/docs/captions/api",
  "openai-whisper": "https://www.remotion.dev/docs/openai-whisper",
  elevenlabs: "https://www.remotion.dev/docs/elevenlabs",
  "eslint-config-internal": null,
  compositor: null,
  "example-videos": null,
  "promo-pages": null,
  "remotion-media": null,
  "svg-3d-engine": null,
  media: "https://remotion.dev/docs/media",
  "web-renderer": "https://www.remotion.dev/docs/web-renderer/",
  design: "https://www.remotion.dev/design",
  "studio-protocol": "https://www.remotion.dev/docs/studio-protocol",
  "light-leaks": "https://www.remotion.dev/docs/light-leaks",
  "mac-cursors": "https://www.remotion.dev/docs/mac-cursors",
  maptiler: null,
  "rough-notation": "https://www.remotion.dev/docs/rough-notation",
  starburst: "https://www.remotion.dev/docs/starburst",
  vercel: "https://www.remotion.dev/docs/vercel/api",
  sfx: "https://www.remotion.dev/docs/sfx",
  effects: "https://www.remotion.dev/docs/effects/api"
};
// src/parse-spring-easing-config.ts
var DEFAULT_SPRING_EASING = {
  type: "spring",
  allowTail: null,
  damping: 10,
  durationRestThreshold: null,
  mass: 1,
  overshootClamping: false,
  stiffness: 100
};
var isAstNode = (value) => {
  return typeof value === "object" && value !== null && typeof value.type === "string";
};
var getNumericValue = (node) => {
  if (node.type === "NumericLiteral") {
    return typeof node.value === "number" ? node.value : null;
  }
  if (node.type === "UnaryExpression" && (node.operator === "-" || node.operator === "+") && isAstNode(node.argument) && node.argument.type === "NumericLiteral" && typeof node.argument.value === "number") {
    return node.operator === "-" ? -node.argument.value : node.argument.value;
  }
  if (node.type === "TSAsExpression" && isAstNode(node.expression)) {
    return getNumericValue(node.expression);
  }
  return null;
};
var getBooleanValue = (node) => {
  if (node.type === "BooleanLiteral") {
    return typeof node.value === "boolean" ? node.value : null;
  }
  if (node.type === "TSAsExpression" && isAstNode(node.expression)) {
    return getBooleanValue(node.expression);
  }
  return null;
};
var getObjectPropertyName = (prop) => {
  if (prop.computed === true || !isAstNode(prop.key)) {
    return null;
  }
  if (prop.key.type === "Identifier") {
    return typeof prop.key.name === "string" ? prop.key.name : null;
  }
  if (prop.key.type === "StringLiteral") {
    return typeof prop.key.value === "string" ? prop.key.value : null;
  }
  return null;
};
var parseSpringEasingConfig = (node) => {
  if (node === undefined) {
    return { ...DEFAULT_SPRING_EASING };
  }
  if (!isAstNode(node)) {
    return null;
  }
  if (node.type === "TSAsExpression") {
    return parseSpringEasingConfig(node.expression);
  }
  if (node.type !== "ObjectExpression" || !Array.isArray(node.properties)) {
    return null;
  }
  const spring = { ...DEFAULT_SPRING_EASING };
  for (const prop of node.properties) {
    if (!isAstNode(prop) || prop.type !== "ObjectProperty") {
      return null;
    }
    const key = getObjectPropertyName(prop);
    if (!key || !isAstNode(prop.value)) {
      return null;
    }
    if (key === "damping" || key === "mass" || key === "stiffness" || key === "durationRestThreshold") {
      const numericValue = getNumericValue(prop.value);
      if (numericValue === null || !Number.isFinite(numericValue) || numericValue <= 0) {
        return null;
      }
      spring[key] = numericValue;
      continue;
    }
    if (key === "overshootClamping" || key === "allowTail") {
      const booleanValue = getBooleanValue(prop.value);
      if (booleanValue === null) {
        return null;
      }
      spring[key] = booleanValue;
      continue;
    }
    return null;
  }
  return spring;
};
// src/package-name.ts
var nodeBuiltinPackages = new Set([
  "assert",
  "async_hooks",
  "buffer",
  "child_process",
  "cluster",
  "console",
  "constants",
  "crypto",
  "dgram",
  "diagnostics_channel",
  "dns",
  "domain",
  "events",
  "fs",
  "http",
  "http2",
  "https",
  "inspector",
  "module",
  "net",
  "os",
  "path",
  "perf_hooks",
  "process",
  "punycode",
  "querystring",
  "readline",
  "repl",
  "sea",
  "sqlite",
  "stream",
  "string_decoder",
  "sys",
  "test",
  "timers",
  "tls",
  "trace_events",
  "tty",
  "url",
  "util",
  "v8",
  "vm",
  "wasi",
  "worker_threads",
  "zlib"
]);
var scopedPackagePattern = /^(?:@([^/]+?)\/)?([^/]+?)$/;
var reservedPackageNames = new Set(["node_modules", "favicon.ico"]);
var isValidPackageName = (packageName) => {
  if (packageName.length === 0 || packageName.length > 214 || packageName.startsWith(".") || packageName.startsWith("_") || packageName.startsWith("-") || packageName.trim() !== packageName || packageName.toLowerCase() !== packageName || reservedPackageNames.has(packageName) || nodeBuiltinPackages.has(packageName) || /[~'!()*]/.test(packageName.split("/").at(-1) ?? "")) {
    return false;
  }
  if (encodeURIComponent(packageName) === packageName) {
    return true;
  }
  const match = packageName.match(scopedPackagePattern);
  if (!match) {
    return false;
  }
  const [, scope, name] = match;
  return scope !== undefined && name !== undefined && encodeURIComponent(scope) === scope && encodeURIComponent(name) === name;
};

// src/required-package.ts
var getRequiredPackageForImportPath = (importPath) => {
  if (importPath === "remotion" || importPath.startsWith(".")) {
    return null;
  }
  if (importPath.startsWith("@")) {
    const [scope, scopedPackageName] = importPath.split("/");
    const scopedPackage = scope && scopedPackageName ? `${scope}/${scopedPackageName}` : null;
    return scopedPackage && isValidPackageName(scopedPackage) ? scopedPackage : null;
  }
  const [packageName] = importPath.split("/");
  return packageName && isValidPackageName(packageName) ? packageName : null;
};
var getRequiredPackageForInsertableElement = (element) => {
  if (element.type === "solid" || element.type === "svg" || element.type === "text") {
    return null;
  }
  if (element.type === "component") {
    return getRequiredPackageForImportPath(element.importPath);
  }
  if (element.type === "composition") {
    return null;
  }
  if (element.assetType === "video" || element.assetType === "audio") {
    return "@remotion/media";
  }
  if (element.assetType === "gif") {
    return "@remotion/gif";
  }
  if (element.assetType === "animated-image") {
    return null;
  }
  return null;
};
var getRequiredPackageForEffectImportPath = (importPath) => {
  if (importPath.startsWith("@remotion/effects/")) {
    return "@remotion/effects";
  }
  if (importPath === "@remotion/light-leaks" || importPath === "@remotion/starburst") {
    return importPath;
  }
  return null;
};
// src/schema-field-info.ts
import { Internals as Internals3 } from "remotion";
import { NoReactInternals as NoReactInternals2 } from "remotion/no-react";

// src/style-property-relations.ts
var BORDER_RADIUS_SHORTHAND_KEY = "style.borderRadius";
var BORDER_RADIUS_LONGHAND_KEYS = [
  "style.borderTopLeftRadius",
  "style.borderTopRightRadius",
  "style.borderBottomRightRadius",
  "style.borderBottomLeftRadius"
];
var STYLE_PROPERTY_LONGHANDS = new Map([
  [BORDER_RADIUS_SHORTHAND_KEY, BORDER_RADIUS_LONGHAND_KEYS]
]);
var getStylePropertyLonghandKeys = (fieldKey) => STYLE_PROPERTY_LONGHANDS.get(fieldKey) ?? [];

// src/schema-field-info.ts
var SCHEMA_FIELD_ROW_HEIGHT = 22;
var SCHEMA_FIELD_GROUPS = [
  { id: "source", label: "Source" },
  { id: "controls", label: "Controls" },
  { id: "transforms", label: "Transform" },
  { id: "text", label: "Text" },
  { id: "background", label: "Background" },
  { id: "border", label: "Border" },
  { id: "border-radius", label: "Border radius" },
  { id: "crop", label: "Crop" },
  { id: "layout", label: "Layout" }
];
var schemaFieldGroupOrder = SCHEMA_FIELD_GROUPS.reduce((acc, group, index) => {
  acc[group.id] = index;
  return acc;
}, {});
var TRANSFORM_FIELD_KEYS = new Set([
  "style.transformOrigin",
  "style.translate",
  "style.scale",
  "style.rotate",
  "style.opacity"
]);
var CROP_FIELD_KEYS = new Set([
  "cropLeft",
  "cropRight",
  "cropTop",
  "cropBottom"
]);
var BORDER_RADIUS_FIELD_KEYS = new Set([
  "style.borderRadius",
  "style.borderTopLeftRadius",
  "style.borderTopRightRadius",
  "style.borderBottomRightRadius",
  "style.borderBottomLeftRadius"
]);
var getBorderRadiusFieldKeysToShow = ({
  activeSchema,
  propStatuses,
  nodePath
}) => {
  if (!(BORDER_RADIUS_SHORTHAND_KEY in activeSchema)) {
    return null;
  }
  const statuses = Internals3.getPropStatusesCtx(propStatuses, nodePath);
  const shorthand = statuses?.[BORDER_RADIUS_SHORTHAND_KEY];
  const longhands = BORDER_RADIUS_LONGHAND_KEYS.map((key) => statuses?.[key]);
  if (shorthand?.status === "keyframed") {
    return new Set([BORDER_RADIUS_SHORTHAND_KEY]);
  }
  if (shorthand?.status === "static" && shorthand.codeValue !== undefined) {
    return new Set([BORDER_RADIUS_SHORTHAND_KEY]);
  }
  const hasEditableLonghand = longhands.some((status) => status?.status === "keyframed" || status?.status === "static" && status.codeValue !== undefined);
  if (hasEditableLonghand) {
    return new Set(BORDER_RADIUS_LONGHAND_KEYS);
  }
  return new Set([BORDER_RADIUS_SHORTHAND_KEY]);
};
var BORDER_FIELD_KEYS = new Set([
  "style.borderWidth",
  "style.borderStyle",
  "style.borderColor"
]);
var BACKGROUND_FIELD_KEYS = new Set(["style.backgroundColor"]);
var LAYOUT_FIELD_KEYS = new Set(["layout", "premountFor"]);
var TEXT_FIELD_KEYS = new Set([
  "children",
  "style.color",
  "style.fontFamily",
  "style.fontSize",
  "style.lineHeight",
  "style.fontWeight",
  "style.fontStyle",
  "style.letterSpacing",
  "style.textAlign"
]);
var getSchemaFieldGroup = (key) => {
  if (key === "src") {
    return "source";
  }
  if (TRANSFORM_FIELD_KEYS.has(key)) {
    return "transforms";
  }
  if (CROP_FIELD_KEYS.has(key)) {
    return "crop";
  }
  if (BORDER_RADIUS_FIELD_KEYS.has(key)) {
    return "border-radius";
  }
  if (BORDER_FIELD_KEYS.has(key)) {
    return "border";
  }
  if (BACKGROUND_FIELD_KEYS.has(key)) {
    return "background";
  }
  if (LAYOUT_FIELD_KEYS.has(key)) {
    return "layout";
  }
  if (TEXT_FIELD_KEYS.has(key)) {
    return "text";
  }
  return "controls";
};
var sortSchemaFields = (fields) => {
  return fields.map((field, index) => ({ field, index })).sort((a, b) => {
    const groupDiff = schemaFieldGroupOrder[a.field.group] - schemaFieldGroupOrder[b.field.group];
    return groupDiff === 0 ? a.index - b.index : groupDiff;
  }).map(({ field }) => field);
};
var TIMELINE_SCHEMA_FIELD_TYPE_SUPPORT = {
  array: true,
  asset: true,
  boolean: true,
  "remotion-captions": false,
  color: true,
  enum: true,
  "font-family": true,
  "font-weight": true,
  hidden: false,
  number: true,
  "rotation-css": true,
  "rotation-degrees": true,
  scale: true,
  "svg-path": true,
  string: true,
  "text-content": true,
  "transform-origin": true,
  translate: true,
  "uv-coordinate": true
};
var isTimelineSchemaFieldSupported = (field) => TIMELINE_SCHEMA_FIELD_TYPE_SUPPORT[field.type];
var getArrayRowCount = ({
  fieldSchema,
  value
}) => {
  const items = Array.isArray(value) ? value : Array.isArray(fieldSchema.default) ? fieldSchema.default : Array.from({ length: fieldSchema.minLength ?? 0 });
  const canAdd = items.length < (fieldSchema.maxLength ?? Infinity);
  return Math.max(1, items.length + (canAdd ? 1 : 0));
};
var getSchemaFieldRowHeight = ({
  fieldSchema,
  value
}) => {
  if (fieldSchema.type === "array") {
    return getArrayRowCount({
      fieldSchema,
      value
    }) * SCHEMA_FIELD_ROW_HEIGHT;
  }
  if (fieldSchema.type === "asset" && fieldSchema.assetType === "image") {
    return 56;
  }
  return SCHEMA_FIELD_ROW_HEIGHT;
};
var getEffectFieldValue = ({
  key,
  dragOverrides,
  effectStatus
}) => {
  const dragOverride = Internals3.getStaticDragOverrideValue(dragOverrides[key]);
  if (dragOverride !== undefined) {
    return dragOverride;
  }
  if (effectStatus?.type !== "can-update-effect") {
    return;
  }
  const propStatus = effectStatus.props[key];
  if (propStatus?.status !== "static") {
    return;
  }
  return propStatus.codeValue;
};
var getFieldsToShow = ({
  getDragOverrides,
  propStatuses,
  nodePath,
  schema,
  currentRuntimeValueDotNotation,
  includeTextContent
}) => {
  const { merged: valuesDotNotation } = Internals3.computeEffectiveSchemaValuesDotNotation({
    schema,
    currentValue: currentRuntimeValueDotNotation,
    overrideValues: getDragOverrides(nodePath),
    propStatus: Internals3.getPropStatusesCtx(propStatuses, nodePath),
    frame: null
  });
  const activeSchema = Internals3.flattenActiveSchema(schema, (key) => valuesDotNotation[key]);
  const borderRadiusFieldKeysToShow = getBorderRadiusFieldKeysToShow({
    activeSchema,
    propStatuses,
    nodePath
  });
  const fields = Object.entries(activeSchema).map(([key, fieldSchema]) => {
    if (BORDER_RADIUS_FIELD_KEYS.has(key) && borderRadiusFieldKeysToShow !== null && !borderRadiusFieldKeysToShow.has(key)) {
      return null;
    }
    if (!isTimelineSchemaFieldSupported(fieldSchema)) {
      return null;
    }
    const typeName = fieldSchema.type;
    if (fieldSchema.type === "number" && fieldSchema.hiddenFromList) {
      return null;
    }
    if ((fieldSchema.type === "string" || fieldSchema.type === "text-content") && !includeTextContent) {
      return null;
    }
    if (key === "hidden") {
      return null;
    }
    return {
      kind: "sequence-field",
      key,
      description: fieldSchema.description,
      typeName,
      rowHeight: getSchemaFieldRowHeight({
        fieldSchema,
        value: valuesDotNotation[key]
      }),
      fieldSchema,
      group: getSchemaFieldGroup(key)
    };
  }).filter(NoReactInternals2.truthy);
  return sortSchemaFields(fields);
};
var getEffectFieldsToShow = ({
  effect,
  effectIndex,
  nodePath,
  propStatuses,
  getEffectDragOverrides
}) => {
  const effectStatus = nodePath === null ? null : Internals3.getEffectPropStatusesCtx({
    propStatuses,
    nodePath,
    effectIndex
  });
  const dragOverrides = nodePath === null ? {} : getEffectDragOverrides(nodePath, effectIndex);
  const activeSchema = Internals3.flattenActiveSchema(effect.schema, (key) => {
    return getEffectFieldValue({ key, dragOverrides, effectStatus });
  });
  const fields = Object.entries(activeSchema).map(([key, fieldSchema]) => {
    if (!isTimelineSchemaFieldSupported(fieldSchema)) {
      return null;
    }
    const typeName = fieldSchema.type;
    if (fieldSchema.type === "number" && fieldSchema.hiddenFromList) {
      return null;
    }
    if (key === "disabled") {
      return null;
    }
    return {
      kind: "effect-field",
      key,
      description: fieldSchema.description,
      typeName,
      rowHeight: getSchemaFieldRowHeight({
        fieldSchema,
        value: getEffectFieldValue({ key, dragOverrides, effectStatus })
      }),
      fieldSchema,
      effectSchema: effect.schema,
      effectIndex,
      group: getSchemaFieldGroup(key)
    };
  }).filter(NoReactInternals2.truthy);
  return sortSchemaFields(fields);
};
// src/stringify-default-props.ts
import { NoReactInternals as NoReactInternals3 } from "remotion/no-react";
function replacerWithPath(replacer) {
  const m = new Map;
  return function(field, value) {
    const path = [m.get(this), field].flat(1);
    if (value === Object(value)) {
      m.set(value, path);
    }
    return replacer.call(this, field, value, path.filter((item) => typeof item !== "undefined" && item !== ""));
  };
}
var doesMatchPath = (path1, enumPaths) => {
  return enumPaths.some((p) => path1.length === p.length && path1.every((item, index) => {
    if (p[index] === "[]" && !Number.isNaN(Number(item))) {
      return true;
    }
    if (p[index] === "{}" && typeof item === "string") {
      return true;
    }
    return item === p[index];
  }));
};
var stringifyDefaultProps = ({
  props,
  enumPaths
}) => {
  return JSON.stringify(props, replacerWithPath(function(key, value, path) {
    const item = this[key];
    if (typeof item === "string" && doesMatchPath(path, enumPaths)) {
      return `${item}__ADD_AS_CONST__`;
    }
    if (doesMatchPath(path, enumPaths)) {
      return `__REMOVEQUOTE__${JSON.stringify(item)}__ADD_AS_LITERAL_CONST__`;
    }
    if (typeof item === "string" && item.startsWith(NoReactInternals3.FILE_TOKEN)) {
      return `__REMOVEQUOTE____WRAP_IN_STATIC_FILE_START__${decodeURIComponent(item.replace(NoReactInternals3.FILE_TOKEN, ""))}__WRAP_IN_STATIC_FILE_END____REMOVEQUOTE__`;
    }
    if (typeof item === "string" && item.startsWith(NoReactInternals3.DATE_TOKEN)) {
      return `__REMOVEQUOTE____WRAP_IN_DATE_START__${decodeURIComponent(item.replace(NoReactInternals3.DATE_TOKEN, ""))}__WRAP_IN_DATE_END____REMOVEQUOTE__`;
    }
    return value;
  })).replace(/"__REMOVEQUOTE__/g, "").replace(/__REMOVEQUOTE__"/g, "").replace(/__ADD_AS_CONST__"/g, '" as const').replace(/__ADD_AS_LITERAL_CONST__"/g, " as const").replace(/__WRAP_IN_STATIC_FILE_START__/g, 'staticFile("').replace(/__WRAP_IN_STATIC_FILE_END__/g, '")').replace(/__WRAP_IN_DATE_START__/g, 'new Date("').replace(/__WRAP_IN_DATE_END__/g, '")');
};
// src/studio-entry-points.ts
var getStudioEntryPoints = ({
  fastRefreshRuntime,
  reactScan,
  environmentSetup,
  sequenceStackTraces,
  studioBootstrap,
  userDefinedComponent,
  reactShim,
  studioRenderEntry
}) => [
  fastRefreshRuntime,
  reactScan,
  environmentSetup,
  sequenceStackTraces,
  studioBootstrap,
  userDefinedComponent,
  reactShim,
  studioRenderEntry
].filter(Boolean);
// src/studio-html.ts
import { Internals as Internals4, VERSION as VERSION2 } from "remotion";
var studioHtml = ({
  publicPath,
  editorName,
  inputProps,
  envVariables,
  staticHash,
  outputHash,
  remotionRoot,
  studioServerCommand,
  renderQueue,
  completedClientRenders,
  numberOfAudioTags,
  publicFiles,
  includeFavicon,
  title,
  renderDefaults,
  publicFolderExists,
  fileSystemPlatform,
  gitSource,
  projectName,
  installedDependencies,
  packageManager,
  audioLatencyHint,
  experimentalKeepAudioContextAlive,
  sampleRate,
  logLevel,
  mode,
  bundleScriptUrl,
  bundleScriptType,
  importMap,
  readOnlyStudio,
  studioRuntimeConfig
}) => {
  const scriptUrl = bundleScriptUrl ?? `${publicPath}bundle.js`;
  const isRelativeBundle = mode === "bundle" && publicPath === "./";
  const staticBaseValue = isRelativeBundle ? `new URL(${JSON.stringify(staticHash)}, window.location.href).pathname` : JSON.stringify(staticHash);
  const staticFilesValue = isRelativeBundle ? `${JSON.stringify(publicFiles)}.map((file) => ({...file, src: new URL(file.src, window.location.href).pathname}))` : JSON.stringify(publicFiles);
  const publicFolderExistsValue = isRelativeBundle && publicFolderExists ? `new URL(${JSON.stringify(publicFolderExists)}, window.location.href).pathname` : JSON.stringify(publicFolderExists);
  const startupErrorHtml = mode === "dev" ? `
		<style>
			#remotion-studio-startup-reload { appearance: none; background: transparent; border: none; border-radius: 3px; color: #a6a7a9; cursor: default; padding: 8px 16px; font: inherit; }
			#remotion-studio-startup-reload:hover { color: white; }
			#remotion-studio-startup-reload:focus { outline: none; box-shadow: none; }
			#remotion-studio-startup-reload:focus-visible { box-shadow: inset 1px 1px #555, inset -1px -1px #555, inset 1px -1px #555, inset -1px 1px #555; }
		</style>
		<div id="remotion-studio-startup-error" role="alert" hidden style="position: fixed; inset: 0; overflow: auto; z-index: 2147483647; box-sizing: border-box; padding: 40px; background: #1f1f1f; color: white; font-family: sans-serif;">
			<h1 style="margin-top: 0; font-size: 24px;">Studio could not start</h1>
			<pre id="remotion-studio-startup-error-message" style="white-space: pre-wrap; overflow-wrap: anywhere; font-size: 14px; line-height: 1.5;"></pre>
			<p>Fix the error in your project, then reload Studio.</p>
			<button id="remotion-studio-startup-reload" type="button">Reload Studio</button>
		</div>
		<script>
			(function () {
				var errors = [];
				var fallback = document.getElementById('remotion-studio-startup-error');
				var message = document.getElementById('remotion-studio-startup-error-message');
				function showError(error) {
					var normalized = error instanceof Error ? error : new Error(String(error));
					errors.push(normalized);
					message.textContent = normalized.stack || normalized.message;
					fallback.hidden = false;
				}
				function onError(event) {
					if (event.target instanceof HTMLScriptElement && event.target.id === '__remotion_bundle') {
						showError(new Error('Could not load the Studio JavaScript bundle. Check that the Studio server is running and reload the page.'));
					} else if (event instanceof ErrorEvent) {
						showError(event.error || new Error(event.message));
					}
				}
				function onUnhandledRejection(event) {
					showError(event.reason);
				}
				window.addEventListener('error', onError, true);
				window.addEventListener('unhandledrejection', onUnhandledRejection);
				document.getElementById('remotion-studio-startup-reload').addEventListener('click', function () {
					if (typeof window.remotion_browserStudioReload === 'function') {
						window.remotion_browserStudioReload();
					} else {
						window.location.reload();
					}
				});
				window.remotion_studioStartup = {
					takeErrors: function () {
						return errors.splice(0);
					},
					dismiss: function () {
						window.removeEventListener('error', onError, true);
						window.removeEventListener('unhandledrejection', onUnhandledRejection);
						errors.length = 0;
						fallback.remove();
						window.remotion_studioStartup = null;
					}
				};
			})();
		</script>` : "";
  return `
<!DOCTYPE html>
<html lang="en">
	<head>
		<meta charset="UTF-8" />
		<meta name="viewport" content="width=device-width, initial-scale=1.0" />
		${importMap ? `<script type="importmap">${JSON.stringify({ imports: importMap }).replaceAll("<", "\\u003c")}</script>` : ""}
		${includeFavicon ? `<link id="__remotion_favicon" rel="icon" type="image/png" href="${publicPath}favicon.ico" />` : ""}
		<title>${title}</title>
	</head>
	<body>
		${startupErrorHtml}
		<script>window.remotion_numberOfAudioTags = ${numberOfAudioTags};</script>
		<script>window.remotion_audioLatencyHint = "${audioLatencyHint}";</script>
		<script>window.remotion_experimentalKeepAudioContextAlive = ${experimentalKeepAudioContextAlive};</script>
		<script>window.remotion_sampleRate = window.remotion_sampleRate ?? ${sampleRate};</script>
		<script>window.remotion_previewSampleRate = ${sampleRate};</script>
		${mode === "dev" ? `<script>window.remotion_logLevel = "${logLevel}";</script>` : ""}
		<script>window.remotion_staticBase = ${staticBaseValue};</script>
		<script>window.remotion_outputsBase = ${JSON.stringify(outputHash)};</script>
		${editorName ? `<script>window.remotion_editorName = "${editorName}";</script>` : "<script>window.remotion_editorName = null;</script>"}
		<script>window.remotion_projectName = ${JSON.stringify(projectName)};</script>
		<script>window.remotion_publicPath = ${JSON.stringify(publicPath)};</script>
		<script>window.remotion_audioEnabled = true;</script>
		<script>window.remotion_videoEnabled = true;</script>
		<script>window.remotion_studioConfig = ${JSON.stringify(studioRuntimeConfig ?? null)};</script>
		<script>window.remotion_renderDefaults = ${JSON.stringify(renderDefaults)};</script>
		<script>window.remotion_cwd = ${JSON.stringify(remotionRoot)};</script>
		<script>window.remotion_fileSystemPlatform = ${JSON.stringify(fileSystemPlatform)};</script>
		<script>window.remotion_studioServerCommand = ${studioServerCommand ? JSON.stringify(studioServerCommand) : "null"};</script>
		${inputProps ? `<script>window.remotion_inputProps = ${JSON.stringify(JSON.stringify(inputProps))};</script>` : ""}
		${renderQueue ? `<script>window.remotion_initialRenderQueue = ${JSON.stringify(renderQueue)};</script>` : ""}
		${completedClientRenders ? `<script>window.remotion_initialClientRenders = ${JSON.stringify(completedClientRenders)};</script>` : ""}
		${envVariables ? `<script>window.process = {env: ${JSON.stringify(envVariables)}};</script>` : ""}
		${gitSource ? `<script>window.remotion_gitSource = ${JSON.stringify(gitSource)};</script>` : ""}
		${mode === "dev" ? `
		<script>window.remotion_isStudio = true;</script>
		<script>window.remotion_isReadOnlyStudio = ${readOnlyStudio ? "true" : "false"};</script>`.trimStart() : ""}
		<script>window.remotion_staticFiles = ${staticFilesValue}</script>
		<script>window.remotion_installedPackages = ${JSON.stringify(installedDependencies)}</script>
		<script>window.remotion_packageManager = ${JSON.stringify(packageManager)}</script>
		<script>window.remotion_publicFolderExists = ${publicFolderExistsValue};</script>
		<script>
				// Increment this value when the generated bundle format or behavior changes
				// in a backwards-incompatible way. It is not the Remotion package version
				// and should not be bumped for every generated HTML change.
				// Keep it synchronized with requiredVersion in
				// packages/renderer/src/set-props-and-env.ts by incrementing both values.
				window.siteVersion = '11';
				window.remotion_version = '${VERSION2}';
		</script>
		
		<div id="video-container"></div>
		<div id="${Internals4.REMOTION_STUDIO_CONTAINER_ELEMENT}"></div>
		<div id="remotion-error-overlay"></div>
		<div id="server-disconnected-overlay"></div>
		<div id="menuportal-0"></div>
		<div id="menuportal-1"></div>
		<div id="menuportal-2"></div>
		<div id="menuportal-3"></div>
		<div id="menuportal-4"></div>
		<div id="menuportal-5"></div>
		<script${mode === "dev" ? ' id="__remotion_bundle"' : ""}${bundleScriptType === "module" ? ' type="module"' : ""} src="${scriptUrl}"></script>
	</body>
</html>
`.trim();
};
// src/update-source-prop-status.ts
import { NoReactInternals as NoReactInternals4 } from "remotion/no-react";
var updateSourcePropStatus = ({
  source,
  videoConfigValues,
  update
}) => {
  const evaluated = NoReactInternals4.evaluateSourcePropStatuses({ value: source }, videoConfigValues).value;
  const next = update(evaluated);
  if (next === evaluated)
    return source;
  if (next.status === "computed" || source.status === "computed")
    return next;
  const timing = {
    keyframeDisplayOffsetAdjustment: source.keyframeDisplayOffsetAdjustment,
    ...source.keyframePlaybackRateAdjustment === undefined ? {} : {
      keyframePlaybackRateAdjustment: source.keyframePlaybackRateAdjustment
    }
  };
  if (next.status !== "keyframed" || source.status !== "keyframed" || evaluated.status !== "keyframed") {
    return { ...next, ...timing };
  }
  return {
    ...next,
    ...timing,
    keyframes: next.keyframes.map((keyframe) => {
      const index = evaluated.keyframes.findIndex((original) => original.frame === keyframe.frame && original.frameExpression === keyframe.frameExpression);
      return index === -1 ? keyframe : { ...keyframe, frame: source.keyframes[index].frame };
    })
  };
};

// src/optimistic-add-keyframe.ts
var getEasingIndexToDuplicate = ({
  insertedKeyframeIndex,
  easingLength,
  keyframeCount
}) => {
  const isSplittingExistingSegment = insertedKeyframeIndex > 0 && insertedKeyframeIndex < keyframeCount - 1;
  if (!isSplittingExistingSegment || easingLength === 0) {
    return null;
  }
  return Math.min(insertedKeyframeIndex - 1, easingLength - 1);
};
var addKeyframeToPropStatus = ({
  status,
  fieldKey,
  frame,
  value,
  schema
}) => {
  if (status.status === "keyframed") {
    const defaultEasing = isSchemaFieldHoldOnly({
      schema,
      key: fieldKey
    }) ? HOLD_KEYFRAME_EASING : LINEAR_KEYFRAME_EASING;
    const existingIndex = status.keyframes.findIndex((kf) => kf.frame === frame);
    if (existingIndex !== -1) {
      const updatedKeyframes = status.keyframes.map((keyframe, index) => index === existingIndex ? { frame, value } : keyframe);
      return {
        ...status,
        keyframes: updatedKeyframes
      };
    }
    const keyframes = [...status.keyframes, { frame, value }].sort((first, second) => first.frame - second.frame);
    const easing = [...status.easing];
    const insertedKeyframeIndex = keyframes.findIndex((keyframe) => keyframe.frame === frame);
    const easingIndexToDuplicate = getEasingIndexToDuplicate({
      insertedKeyframeIndex,
      easingLength: easing.length,
      keyframeCount: keyframes.length
    });
    const easingToDuplicate = easingIndexToDuplicate === null ? defaultEasing : easing[easingIndexToDuplicate];
    easing.splice(insertedKeyframeIndex, 0, easingToDuplicate);
    while (easing.length < keyframes.length - 1) {
      easing.push(defaultEasing);
    }
    return {
      ...status,
      keyframes,
      easing
    };
  }
  if (status.status === "static") {
    const staticValue = status.codeValue ?? value;
    return {
      status: "keyframed",
      keyframeDisplayOffsetAdjustment: status.keyframeDisplayOffsetAdjustment,
      ...status.keyframePlaybackRateAdjustment === undefined ? {} : {
        keyframePlaybackRateAdjustment: status.keyframePlaybackRateAdjustment
      },
      interpolationFunction: getKeyframeInterpolationFunction({
        schema,
        key: fieldKey,
        staticValue,
        newValue: value
      }),
      keyframes: [{ frame, value }],
      easing: [],
      clamping: { left: "clamp", right: "clamp" },
      posterize: undefined,
      output: undefined
    };
  }
  return status;
};
var findFieldInSchema2 = (schema, key) => {
  if (key in schema) {
    return schema[key];
  }
  for (const field of Object.values(schema)) {
    if (field.type !== "enum") {
      continue;
    }
    for (const variant of Object.values(field.variants)) {
      const found = findFieldInSchema2(variant, key);
      if (found) {
        return found;
      }
    }
  }
  return;
};
var getMissingPropStatus = ({
  schema,
  fieldKey
}) => {
  const field = schema ? findFieldInSchema2(schema, fieldKey) : undefined;
  if (field && field.type !== "hidden" && field.default !== undefined) {
    return {
      status: "static",
      keyframeDisplayOffsetAdjustment: null,
      codeValue: field.default
    };
  }
  return {
    status: "static",
    keyframeDisplayOffsetAdjustment: null,
    codeValue: undefined
  };
};
var optimisticAddSequenceKeyframe = ({
  previous,
  videoConfigValues,
  fieldKey,
  frame,
  value,
  schema
}) => {
  if (!previous.canUpdate) {
    return previous;
  }
  if (!isSchemaFieldKeyframable({ schema: schema ?? null, key: fieldKey })) {
    return previous;
  }
  const status = previous.props[fieldKey] ?? getMissingPropStatus({ schema: schema ?? null, fieldKey });
  return {
    ...previous,
    props: {
      ...previous.props,
      [fieldKey]: updateSourcePropStatus({
        source: status,
        videoConfigValues,
        update: (evaluatedStatus) => addKeyframeToPropStatus({
          status: evaluatedStatus,
          fieldKey,
          frame,
          value,
          schema: schema ?? null
        })
      })
    }
  };
};
var optimisticAddEffectKeyframe = ({
  previous,
  videoConfigValues,
  effectIndex,
  fieldKey,
  frame,
  value,
  schema
}) => {
  if (!previous.canUpdate) {
    return previous;
  }
  if (!isSchemaFieldKeyframable({ schema: schema ?? null, key: fieldKey })) {
    return previous;
  }
  const targetIndex = previous.effects.findIndex((e) => e.effectIndex === effectIndex);
  if (targetIndex === -1) {
    return previous;
  }
  const target = previous.effects[targetIndex];
  if (!target.canUpdate) {
    return previous;
  }
  const status = target.props[fieldKey] ?? getMissingPropStatus({ schema: schema ?? null, fieldKey });
  const updatedEffect = {
    ...target,
    props: {
      ...target.props,
      [fieldKey]: updateSourcePropStatus({
        source: status,
        videoConfigValues,
        update: (evaluatedStatus) => addKeyframeToPropStatus({
          status: evaluatedStatus,
          fieldKey,
          frame,
          value,
          schema: schema ?? null
        })
      })
    }
  };
  const effects = [...previous.effects];
  effects[targetIndex] = updatedEffect;
  return {
    ...previous,
    effects
  };
};
// src/optimistic-delete-keyframe.ts
var getEasingIndexToRemove = ({
  removedKeyframeIndex,
  keyframeCountBeforeRemoval
}) => {
  if (removedKeyframeIndex === 0) {
    return 0;
  }
  if (removedKeyframeIndex === keyframeCountBeforeRemoval - 1) {
    return removedKeyframeIndex - 1;
  }
  return removedKeyframeIndex;
};
var removeKeyframeFromPropStatus = ({
  status,
  frame,
  valueWhenLastKeyframeDeleted
}) => {
  if (status.status !== "keyframed") {
    return status;
  }
  const index = status.keyframes.findIndex((kf) => kf.frame === frame);
  if (index === -1) {
    return status;
  }
  const keyframes = status.keyframes.filter((_, i) => i !== index);
  if (keyframes.length === 0) {
    return {
      status: "static",
      keyframeDisplayOffsetAdjustment: status.keyframeDisplayOffsetAdjustment,
      ...status.keyframePlaybackRateAdjustment === undefined ? {} : {
        keyframePlaybackRateAdjustment: status.keyframePlaybackRateAdjustment
      },
      codeValue: valueWhenLastKeyframeDeleted === null ? status.keyframes[index].value : valueWhenLastKeyframeDeleted
    };
  }
  const easing = [...status.easing];
  if (easing.length > 0) {
    const easingIndexToRemove = getEasingIndexToRemove({
      removedKeyframeIndex: index,
      keyframeCountBeforeRemoval: status.keyframes.length
    });
    easing.splice(easingIndexToRemove, 1);
  }
  return {
    ...status,
    keyframes,
    easing
  };
};
var optimisticDeleteSequenceKeyframe = ({
  previous,
  videoConfigValues,
  fieldKey,
  frame,
  valueWhenLastKeyframeDeleted
}) => {
  if (!previous.canUpdate) {
    return previous;
  }
  const status = previous.props[fieldKey];
  if (!status) {
    return previous;
  }
  return {
    ...previous,
    props: {
      ...previous.props,
      [fieldKey]: updateSourcePropStatus({
        source: status,
        videoConfigValues,
        update: (evaluatedStatus) => removeKeyframeFromPropStatus({
          status: evaluatedStatus,
          frame,
          valueWhenLastKeyframeDeleted: valueWhenLastKeyframeDeleted ?? null
        })
      })
    }
  };
};
var optimisticDeleteSequenceKeyframes = ({
  previous,
  videoConfigValues,
  keyframes
}) => {
  return keyframes.reduce((current, keyframe) => optimisticDeleteSequenceKeyframe({
    previous: current,
    videoConfigValues,
    fieldKey: keyframe.fieldKey,
    frame: keyframe.frame,
    valueWhenLastKeyframeDeleted: keyframe.valueWhenLastKeyframeDeleted
  }), previous);
};
var optimisticDeleteEffectKeyframe = ({
  previous,
  videoConfigValues,
  effectIndex,
  fieldKey,
  frame,
  valueWhenLastKeyframeDeleted
}) => {
  if (!previous.canUpdate) {
    return previous;
  }
  const targetIndex = previous.effects.findIndex((e) => e.effectIndex === effectIndex);
  if (targetIndex === -1) {
    return previous;
  }
  const target = previous.effects[targetIndex];
  if (!target.canUpdate) {
    return previous;
  }
  const status = target.props[fieldKey];
  if (!status) {
    return previous;
  }
  const updatedEffect = {
    ...target,
    props: {
      ...target.props,
      [fieldKey]: updateSourcePropStatus({
        source: status,
        videoConfigValues,
        update: (evaluatedStatus) => removeKeyframeFromPropStatus({
          status: evaluatedStatus,
          frame,
          valueWhenLastKeyframeDeleted: valueWhenLastKeyframeDeleted ?? null
        })
      })
    }
  };
  const effects = [...previous.effects];
  effects[targetIndex] = updatedEffect;
  return {
    ...previous,
    effects
  };
};
var optimisticDeleteEffectKeyframes = ({
  previous,
  videoConfigValues,
  keyframes
}) => {
  return keyframes.reduce((current, keyframe) => optimisticDeleteEffectKeyframe({
    previous: current,
    videoConfigValues,
    effectIndex: keyframe.effectIndex,
    fieldKey: keyframe.fieldKey,
    frame: keyframe.frame,
    valueWhenLastKeyframeDeleted: keyframe.valueWhenLastKeyframeDeleted
  }), previous);
};
// src/optimistic-move-keyframe.ts
var getMoveMap = (moves) => {
  const moveMap = new Map;
  for (const move of moves) {
    if (move.fromFrame === move.toFrame) {
      continue;
    }
    if (moveMap.has(move.fromFrame)) {
      return null;
    }
    moveMap.set(move.fromFrame, move.toFrame);
  }
  return moveMap;
};
var getMovedKeyframes = ({
  status,
  moves
}) => {
  if (status.status !== "keyframed") {
    return null;
  }
  const moveMap = getMoveMap(moves);
  if (moveMap === null) {
    return null;
  }
  if (moveMap.size === 0) {
    return { keyframes: status.keyframes, removedKeyframeIndexes: [] };
  }
  const frames = new Set(status.keyframes.map((keyframe) => keyframe.frame));
  for (const fromFrame of moveMap.keys()) {
    if (!frames.has(fromFrame)) {
      return null;
    }
  }
  const movedFromFrames = new Set(moveMap.keys());
  const movedToFrames = new Set(moveMap.values());
  const removedKeyframeIndexes = [];
  const nextKeyframes = status.keyframes.flatMap((keyframe, index) => {
    const movedFrame = moveMap.get(keyframe.frame);
    if (movedFrame !== undefined) {
      return [{ ...keyframe, frame: movedFrame }];
    }
    if (movedToFrames.has(keyframe.frame) && !movedFromFrames.has(keyframe.frame)) {
      removedKeyframeIndexes.push(index);
      return [];
    }
    return [keyframe];
  });
  const nextFrames = new Set;
  for (const keyframe of nextKeyframes) {
    if (nextFrames.has(keyframe.frame)) {
      return null;
    }
    nextFrames.add(keyframe.frame);
  }
  return {
    keyframes: nextKeyframes.sort((a, b) => a.frame - b.frame),
    removedKeyframeIndexes
  };
};
var removeEasingForRemovedKeyframes = ({
  easing,
  removedKeyframeIndexes
}) => {
  const nextEasing = [...easing];
  for (const removedKeyframeIndex of [...removedKeyframeIndexes].sort((a, b) => b - a)) {
    if (nextEasing.length === 0) {
      break;
    }
    const easingIndexToRemove = removedKeyframeIndex === 0 ? 0 : removedKeyframeIndex - 1;
    nextEasing.splice(easingIndexToRemove, 1);
  }
  return nextEasing;
};
var canMoveKeyframesWithoutCollisions = ({
  status,
  moves
}) => {
  return getMovedKeyframes({ status, moves }) !== null;
};
var moveKeyframesInPropStatus = ({
  status,
  moves
}) => {
  if (status.status !== "keyframed") {
    return status;
  }
  const moved = getMovedKeyframes({ status, moves });
  if (moved === null || moved.removedKeyframeIndexes.length === 0 && moved.keyframes === status.keyframes) {
    return status;
  }
  const easing = removeEasingForRemovedKeyframes({
    easing: status.easing,
    removedKeyframeIndexes: moved.removedKeyframeIndexes
  });
  return {
    ...status,
    keyframes: moved.keyframes,
    easing
  };
};
var optimisticMoveSequenceKeyframes = ({
  previous,
  videoConfigValues,
  keyframes
}) => {
  if (!previous.canUpdate) {
    return previous;
  }
  const movesByField = new Map;
  for (const keyframe of keyframes) {
    const moves = movesByField.get(keyframe.fieldKey) ?? [];
    moves.push(keyframe);
    movesByField.set(keyframe.fieldKey, moves);
  }
  const props = { ...previous.props };
  for (const [fieldKey, moves] of movesByField) {
    const status = props[fieldKey];
    if (!status) {
      continue;
    }
    props[fieldKey] = updateSourcePropStatus({
      source: status,
      videoConfigValues,
      update: (evaluatedStatus) => moveKeyframesInPropStatus({ status: evaluatedStatus, moves })
    });
  }
  return {
    ...previous,
    props
  };
};
var optimisticMoveEffectKeyframes = ({
  previous,
  videoConfigValues,
  keyframes
}) => {
  if (!previous.canUpdate) {
    return previous;
  }
  const movesByEffect = new Map;
  for (const keyframe of keyframes) {
    const moves = movesByEffect.get(keyframe.effectIndex) ?? [];
    moves.push(keyframe);
    movesByEffect.set(keyframe.effectIndex, moves);
  }
  const effects = previous.effects.map((effect) => {
    if (!effect.canUpdate) {
      return effect;
    }
    const movesForEffect = movesByEffect.get(effect.effectIndex);
    if (!movesForEffect) {
      return effect;
    }
    const props = { ...effect.props };
    const movesByField = new Map;
    for (const move of movesForEffect) {
      const moves = movesByField.get(move.fieldKey) ?? [];
      moves.push(move);
      movesByField.set(move.fieldKey, moves);
    }
    for (const [fieldKey, moves] of movesByField) {
      const status = props[fieldKey];
      if (!status) {
        continue;
      }
      props[fieldKey] = updateSourcePropStatus({
        source: status,
        videoConfigValues,
        update: (evaluatedStatus) => moveKeyframesInPropStatus({ status: evaluatedStatus, moves })
      });
    }
    return {
      ...effect,
      props
    };
  });
  return {
    ...previous,
    effects
  };
};
// src/optimistic-update-for-effect-prop-statuses.ts
import { NoReactInternals as NoReactInternals5 } from "remotion/no-react";
var optimisticUpdateForEffectPropStatuses = ({
  previous,
  effectIndex,
  fieldKey,
  value,
  schema
}) => {
  if (!previous.canUpdate) {
    return previous;
  }
  const targetIndex = previous.effects.findIndex((e) => e.effectIndex === effectIndex);
  if (targetIndex === -1) {
    return previous;
  }
  const target = previous.effects[targetIndex];
  if (!target.canUpdate) {
    return previous;
  }
  const props = {
    ...target.props,
    [fieldKey]: {
      status: "static",
      keyframeDisplayOffsetAdjustment: null,
      codeValue: value
    }
  };
  if (schema[fieldKey]?.type === "enum") {
    const propsToDelete = NoReactInternals5.findPropsToDelete({
      schema,
      key: fieldKey,
      value
    });
    for (const propToDelete of propsToDelete) {
      delete props[propToDelete];
    }
  }
  const updatedEffect = {
    ...target,
    props
  };
  const effects = [...previous.effects];
  effects[targetIndex] = updatedEffect;
  return {
    ...previous,
    effects
  };
};
// src/optimistic-update-for-prop-statuses.ts
import { NoReactInternals as NoReactInternals6 } from "remotion/no-react";
var optimisticUpdateForPropStatuses = ({
  previous,
  fieldKey,
  value,
  defaultValue,
  schema
}) => {
  if (!previous.canUpdate) {
    return previous;
  }
  const serializedValue = JSON.stringify(value);
  const optimisticValue = defaultValue !== null && defaultValue === serializedValue ? undefined : value;
  const props = {
    ...previous.props,
    [fieldKey]: {
      status: "static",
      keyframeDisplayOffsetAdjustment: null,
      codeValue: optimisticValue
    }
  };
  if (schema[fieldKey]?.type === "enum") {
    const propsToDelete = NoReactInternals6.findPropsToDelete({
      schema,
      key: fieldKey,
      value
    });
    for (const propToDelete of propsToDelete) {
      delete props[propToDelete];
    }
  }
  return {
    ...previous,
    props,
    effects: previous.effects
  };
};
// src/optimistic-update-keyframe-settings.ts
var updateEasing = ({
  easing,
  segmentCount,
  segmentIndex,
  value
}) => {
  if (!Number.isInteger(segmentIndex) || segmentIndex < 0 || segmentIndex >= segmentCount) {
    throw new Error("Cannot update easing: segment index out of range");
  }
  const nextEasing = Array.from({ length: segmentCount }, (_, index) => {
    return easing[index] ?? LINEAR_KEYFRAME_EASING;
  });
  nextEasing[segmentIndex] = value;
  return nextEasing;
};
var applyKeyframeSettingsToStatus = (status, settings) => {
  return {
    ...status,
    ...settings.type === "settings" && settings.clamping ? { clamping: settings.clamping } : {},
    ...settings.type === "settings" ? { posterize: settings.posterize } : {},
    ...settings.type === "settings" ? { output: settings.output } : {},
    ...settings.type === "easing" ? {
      easing: updateEasing({
        easing: status.easing,
        segmentCount: Math.max(0, status.keyframes.length - 1),
        segmentIndex: settings.segmentIndex,
        value: settings.easing
      })
    } : {}
  };
};
var optimisticUpdateSequenceKeyframeSettings = ({
  previous,
  fieldKey,
  settings
}) => {
  if (!previous.canUpdate) {
    return previous;
  }
  const status = previous.props[fieldKey];
  if (!status || status.status !== "keyframed") {
    return previous;
  }
  return {
    ...previous,
    props: {
      ...previous.props,
      [fieldKey]: applyKeyframeSettingsToStatus(status, settings)
    }
  };
};
var optimisticUpdateEffectKeyframeSettings = ({
  previous,
  effectIndex,
  fieldKey,
  settings
}) => {
  if (!previous.canUpdate) {
    return previous;
  }
  const targetIndex = previous.effects.findIndex((effect) => effect.effectIndex === effectIndex);
  if (targetIndex === -1) {
    return previous;
  }
  const target = previous.effects[targetIndex];
  if (!target.canUpdate) {
    return previous;
  }
  const status = target.props[fieldKey];
  if (!status || status.status !== "keyframed") {
    return previous;
  }
  const effects = [...previous.effects];
  effects[targetIndex] = {
    ...target,
    props: {
      ...target.props,
      [fieldKey]: applyKeyframeSettingsToStatus(status, settings)
    }
  };
  return {
    ...previous,
    effects
  };
};
// src/stringify-sequence-subscription-key.ts
var stringifySequenceSubscriptionKey = (key) => {
  return `${key.absolutePath}:${JSON.stringify(key.nodePath)}:${key.sequenceKeys.join("\x00")}:${key.effectKeys.map((keys) => keys.join("\x00")).join("\x00\x00")}`;
};
var stringifySequenceExpandedRowKey = (key) => {
  return `${key.absolutePath}:${JSON.stringify(key.nodePath)}:${key.sequenceKeys.join("\x00")}`;
};
// src/url.ts
var isUrl = (value) => {
  try {
    const parsed = new URL(value);
    return parsed.href.length > 0;
  } catch {
    return false;
  }
};
var normalizeHttpUrl = (value) => {
  if (typeof value !== "string") {
    return null;
  }
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:" ? parsed.href : null;
  } catch {
    return null;
  }
};
// src/empty-composition-component.ts
var emptyCompositionComponent = (componentName) => {
  return `import React from 'react';

export const ${componentName}: React.FC = () => {
	return null;
};
`;
};
// src/asset-composition-component.ts
var assetCompositionComponent = ({
  asset,
  componentName
}) => {
  const tag = asset.type === "image" ? "CanvasImage" : asset.type === "video" ? "Video" : "Audio";
  const imports = asset.type === "image" ? "import {CanvasImage, staticFile} from 'remotion';" : `import {${tag}} from '@remotion/media';
import {staticFile} from 'remotion';`;
  return `import React from 'react';
${imports}

export const ${componentName}: React.FC = () => {
	return (
		<${tag}
			src={staticFile(${JSON.stringify(asset.src)})}
			durationInFrames={${asset.durationInFrames}}
		/>
	);
};
`;
};
export {
  BORDER_RADIUS_LONGHAND_KEYS,
  BORDER_RADIUS_SHORTHAND_KEY,
  CANVAS_CAPTURE_METADATA_TAG,
  CUBIC_KEYFRAME_EASING,
  DEFAULT_BUFFER_STATE_DELAY_IN_MILLISECONDS,
  DEFAULT_SPRING_EASING,
  DEFAULT_TIMELINE_TRACKS,
  EASE_KEYFRAME_EASING,
  EFFECT_CATALOG,
  HOLD_KEYFRAME_EASING,
  KEYFRAME_EASING_PRESETS,
  LINEAR_KEYFRAME_EASING,
  QUAD_KEYFRAME_EASING,
  REACT_REFRESH_FINISHED_EVENT,
  REACT_REFRESH_STARTED_EVENT,
  SCHEMA_FIELD_GROUPS,
  SCHEMA_FIELD_ROW_HEIGHT,
  addKeyframeToPropStatus,
  apiDocs,
  applyKeyframeSettingsToStatus,
  assetCompositionComponent,
  canEditEasingForInterpolationFunction,
  canMoveKeyframesWithoutCollisions,
  configMethodLifecycles,
  descriptions,
  detectFileType,
  emptyCompositionComponent,
  extraPackages,
  formatBytes,
  getAllSchemaKeys,
  getAssetSchemaKeys,
  getBackKeyframeEasing,
  getConfigFileChangeMessage,
  getConflictingOutputPaths,
  getDefaultOutLocation,
  getEffectCatalogCategories,
  getEffectDocumentationLink,
  getEffectDocumentationPath,
  getEffectFieldsToShow,
  getEffectPreviewAlt,
  getEffectPreviewSource,
  getFieldsToShow,
  getKeyframeInterpolationFunction,
  getKeyframeInterpolationFunctionForSchemaField,
  getKeyframeOutputTypeForSchemaField,
  getLocationFromBuildError,
  getOutKeyframeEasing,
  getPolyKeyframeEasing,
  getPortableDownloadFilename,
  getPreferredApp,
  getProjectName,
  getRemoteAssetElement,
  getRemoteAssetFilename,
  getRequiredPackageForEffectImportPath,
  getRequiredPackageForInsertableElement,
  getSchemaFieldGroup,
  getStudioEntryPoints,
  getStylePropertyLonghandKeys,
  hasSequenceTimingTraits,
  hotMiddlewareOptions,
  installableMap,
  isImageFileType,
  isInteractivitySchemaFieldHoldOnly,
  isInteractivitySchemaFieldKeyframable,
  isKeyframeClipboardFieldType,
  isKeyframeInterpolationFunction,
  isSchemaFieldHoldOnly,
  isSchemaFieldKeyframable,
  isUrl,
  isValidPackageName,
  keyframeInterpolationFunctions,
  maxRemoteAssetSize,
  moveKeyframesInPropStatus,
  normalizeHttpUrl,
  optimisticAddEffectKeyframe,
  optimisticAddSequenceKeyframe,
  optimisticDeleteEffectKeyframe,
  optimisticDeleteEffectKeyframes,
  optimisticDeleteSequenceKeyframe,
  optimisticDeleteSequenceKeyframes,
  optimisticMoveEffectKeyframes,
  optimisticMoveSequenceKeyframes,
  optimisticUpdateEffectKeyframeSettings,
  optimisticUpdateForEffectPropStatuses,
  optimisticUpdateForPropStatuses,
  optimisticUpdateSequenceKeyframeSettings,
  packages,
  parseCanvasCaptureData,
  parseEasingClipboardData,
  parseEasingClipboardDataResult,
  parseEffectClipboardData,
  parseEffectClipboardDataResult,
  parseEffectPropClipboardData,
  parseEffectPropClipboardDataResult,
  parseKeyframeClipboardData,
  parseKeyframeClipboardDataResult,
  parseSequencePropClipboardData,
  parseSequencePropClipboardDataResult,
  parseSpringEasingConfig,
  preferredFallbackEditorIds,
  remoteAssetAcceptHeader,
  remoteAssetDownloadTimeout,
  removeKeyframeFromPropStatus,
  splitAnsi,
  stringifyDefaultProps,
  stringifySequenceExpandedRowKey,
  stringifySequenceSubscriptionKey,
  stripAnsi,
  studioHtml,
  studioKeyboardShortcutActions,
  studioOperations,
  validateStudioArtifactFilename,
  validateStudioKeyboardShortcuts
};
